import React, { useEffect, useState, useCallback, useRef, useLayoutEffect } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { debounce } from "lodash";
import { motion, AnimatePresence } from "framer-motion";
import {
    FiArrowLeft,
    FiShield,
    FiDatabase,
    FiLock,
    FiGlobe,
    FiUsers,
    FiClock,
    FiCheckCircle,
    FiInfo,
    FiActivity,
    FiEdit2,
    FiLink,
    FiShare2,
    FiCpu,
    FiRefreshCw,
    FiPlus,
    FiTarget,
    FiDownload,
    FiAlertTriangle,
    FiFileText,
    FiUpload,
    FiSave,
    FiX,
    FiPaperclip,
    FiTrash2,
    FiChevronDown,
    FiSearch,
    FiCheck,
    FiBook,
    FiUploadCloud,
    FiEdit3,
    FiExternalLink,
    FiMoreVertical,
    FiFilter,
    FiCalendar,
    FiChevronRight,
    FiChevronLeft,
    FiUserPlus,
    FiCornerDownLeft
} from "react-icons/fi";
import axios from "../../utils/api/axios";
import usePageTitle from "../../utils/hooks/usePageTitle";
import { format } from "date-fns";
import CreateTargetWizard from "./components/CreateTargetWizard";
import ResourceManager from "./components/ResourceManager";
import EditTargetModal from "./components/EditTargetModal";
import BulkImportModal from "./components/BulkImportModal";
import { toast } from "react-hot-toast";
import useUserCredentials from "../../utils/hooks/user/useUserCredentials";
import { UserRole } from "../../utils/api/endpoints/user/credentials";

interface AssetData {
    id: string;
    company_id?: number | string;
    name: string;
    summary: string;
    risk_owner: string;
    technical_owner: string;
    technical_owner_invite_later: boolean;
    category: string;
    category_other?: string;
    primary_type: string[];
    includes: string[];
    includes_other?: string;
    lifecycle_status: string;
    lifecycle_status_other?: string;
    change_frequency: string;
    change_frequency_other?: string;
    deployment_model: string;
    deployment_model_other?: string;
    primary_audience: string[];
    criticality: string;
    criticality_answers?: Record<string, string>;
    compliance_frameworks: string[];
    compliance_other?: string;
    data_classification: string;
    data_classification_other?: string;
    sensitive_data_description: string;
    data_residency: string[];
    authentication_required: string;
    authorization_model: string[];
    authorization_model_other?: string;
    identity_provider: string;
    user_roles: string;
    internet_exposure: string;
    allowed_testing_types: string[];
    testing_constraints: string;
    legal_ownership_confirmed: boolean;
    approved_by: string | null;
    approval_date: string | null;
    architecture_diagram_url?: string[];
    product_documentation_url?: string[];
    runbook_url?: string[];
    threat_model_url?: string[];
    additional_notes?: string;
    documentation_files?: { name: string; url: string; type: string; category?: string }[];
    status: string;
    is_active_in_run?: boolean | number;
    created_at: string;
    updated_at: string;
    mobile_platforms?: string[];
    primary_type_other?: string;
}

interface ActivityLog {
    id: number;
    user_id: number;
    role: number;
    action: string;
    module: string;
    asset_id: string;
    created_at: string;
    user: {
        display_name: string;
        username: string;
        pfp: string;
    };
}

interface TargetData {
    target_id: string;
    target_name: string;
    target_type: string;
    environment: string;
    target_value: string;
    status: string;
    is_linked: boolean;
    metadata?: {
        binary_url?: string;
        binary_name?: string;
        binary_type?: string;
    };
    resource_count?: number;
    created_at: string;
}

const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
        opacity: 1,
        transition: { staggerChildren: 0.1 }
    }
};

const InfoCard: React.FC<{ title: string; icon: React.ReactNode; iconColor?: string; children: React.ReactNode }> = ({ title, icon, children }) => (
    <div className="bg-white rounded-2xl border border-gray-100 shadow-sm mb-6">
        <div className="px-8 py-5 flex items-center gap-3.5">
            <div className="w-9 h-9 bg-[#ecfdf5] rounded-xl flex items-center justify-center text-[#10b981]">
                {React.cloneElement(icon as React.ReactElement, { className: "w-5 h-5" })}
            </div>
            <h3 className="text-[17px] font-bold text-[#1e293b] tracking-tight">{title}</h3>
        </div>
        <div className="px-8 pb-7 pt-0">
            {children}
        </div>
    </div>
);

const DataRow: React.FC<{ label: string; value: React.ReactNode; fullWidth?: boolean }> = ({ label, value, fullWidth }) => (
    <div className={`${fullWidth ? 'col-span-2' : ''} mb-8 last:mb-0`}>
        <p className="text-[12px] font-medium text-[#94a3b8] mb-1">{label}</p>
        <div className="text-[14px] text-[#1e293b] font-semibold leading-tight">
            {value || <span className="text-gray-400 font-normal italic">Not specified</span>}
        </div>
    </div>
);

const Pill: React.FC<{ children: React.ReactNode }> = ({ children }) => (
    <span className="inline-flex items-center px-3 py-1 bg-[#f1f5f9] text-[#2563eb] rounded-[8px] text-[12px] font-bold mr-2 mb-2">
        {children}
    </span>
);

const ReadMoreText: React.FC<{ text: string; maxLines?: number; className?: string }> = ({ text, maxLines = 4, className = "text-[14px] text-[#1e293b] font-semibold" }) => {
    const [isExpanded, setIsExpanded] = useState(false);
    const [isOverflowing, setIsOverflowing] = useState(false);
    const textRef = useRef<HTMLParagraphElement>(null);

    useEffect(() => {
        const paragraph = textRef.current;
        if (!paragraph) return;

        const checkOverflow = () => {
            if (paragraph) {
                // Check if the content height exceeds the visible height
                // When collapsed: scrollHeight > clientHeight implies overflow
                // When expanded: we can't easily check overflow via scrollHeight vs clientHeight 
                // because they are equal. But we only need to know if it SHOULD have a button.
                // So we need to measure the full scrollHeight and compare it to what the height WOULD be if clamped.

                // However, a simpler reliable way for the collapsed state is:
                const isScrollable = paragraph.scrollHeight > paragraph.clientHeight;

                // If expanded, we want to keep the button visible if it WAS overflowing.
                // But react state handling usually preserves 'isOverflowing' unless we recalculate.
                // To strictly support "re-calculating" even when expanded (e.g. window resize making it NOT overflow),
                // we would need to temporarily collapse it to measure. 
                // For now, let's trust the scrollHeight trigger when collapsed.

                // Optimization: Only update if changed prevents verify loops
                setIsOverflowing(prev => {
                    // If currently expanded, we assume it was overflowing. 
                    // Real-time update while expanded is complex without layout thrashing.
                    if (isExpanded) return true;
                    return isScrollable;
                });
            }
        };

        const observer = new ResizeObserver(() => {
            checkOverflow();
        });

        observer.observe(paragraph);

        // Initial check
        checkOverflow();

        return () => {
            observer.disconnect();
        };
    }, [text, isExpanded, maxLines]);

    // Force re-measure when switching to collapsed to ensure button appears if needed
    useEffect(() => {
        if (!isExpanded && textRef.current) {
            const isScrollable = textRef.current.scrollHeight > textRef.current.clientHeight;
            setIsOverflowing(isScrollable);
        }
    }, [isExpanded]);

    if (!text || text === "None" || text === "None provided" || text === "Not specified") {
        return <>{text || <span className="text-gray-400 font-normal italic">Not specified</span>}</>;
    }

    return (
        <div className="group/readmore">
            <p
                ref={textRef}
                className={`leading-tight whitespace-pre-wrap transition-all duration-300 ${!isExpanded ? 'overflow-hidden' : ''} ${className}`}
                style={!isExpanded ? {
                    display: '-webkit-box',
                    WebkitLineClamp: maxLines,
                    WebkitBoxOrient: 'vertical',
                    overflow: 'hidden'
                } : {}}
            >
                {text}
            </p>
            {(isOverflowing || isExpanded) && (
                <button
                    onClick={(e) => {
                        e.stopPropagation();
                        setIsExpanded(!isExpanded);
                    }}
                    className="text-blue-600 text-[12px] font-semibold hover:text-blue-700 transition-colors mt-1 flex items-center gap-1"
                >
                    {isExpanded ? (
                        <>
                            Read less
                            <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
                            </svg>
                        </>
                    ) : (
                        <>
                            Read more
                            <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                            </svg>
                        </>
                    )}
                </button>
            )}
        </div>
    );
};

const StatusBadge: React.FC<{ 
    asset: AssetData, 
    canEdit: boolean,
    onStatusChange: (assetId: string, newStatus: string) => Promise<void>,
    isHeader?: boolean
}> = ({ asset, canEdit, onStatusChange, isHeader }) => {
    const isActive = asset.status?.toLowerCase() === "active";
    const statusOptions = ["Active", "Draft", "Deactivated"];
    const currentStatus = asset.status ? asset.status.charAt(0).toUpperCase() + asset.status.slice(1).toLowerCase() : "Active";
    
    // Header vs Sidebar styling
    const headerClasses = "px-2.5 py-1 rounded-md text-xs font-semibold";
    const sidebarClasses = "px-4 py-1 rounded-xl text-[11px] font-bold border flex items-center gap-1.5";
    const baseClasses = isHeader ? headerClasses : sidebarClasses;

    const colorClasses = isHeader ? 
        (isActive ? "bg-green-500 text-white" : currentStatus === "Deactivated" ? "bg-red-500 text-white" : "bg-yellow-500 text-white") :
        (isActive ? "bg-[#e2f9eb] text-[#2ba972] border-[#c2f2d6]" : currentStatus === "Deactivated" ? "bg-red-50 text-red-600 border-red-100" : "bg-amber-50 text-amber-600 border-amber-100");

    if (canEdit && !asset.is_active_in_run) {
        return (
            <select
                onClick={(e) => e.stopPropagation()}
                value={currentStatus}
                onChange={(e) => onStatusChange(asset.id, e.target.value)}
                className={`outline-none cursor-pointer appearance-none transition-all focus:ring-2 focus:ring-blue-500/50 ${baseClasses} ${colorClasses}`}
                style={{ textAlignLast: 'center' }}
            >
                {statusOptions.map(s => (
                    <option key={s} value={s} className="bg-white text-gray-800 text-left font-semibold">{s}</option>
                ))}
            </select>
        );
    }

    return (
        <div 
            className="group/status relative inline-flex"
            title={asset.is_active_in_run ? "Status is locked while this asset is in an active or scheduled program run." : ""}
        >
            <span className={`${baseClasses} ${colorClasses} ${asset.is_active_in_run && canEdit ? "opacity-75 ring-1 ring-white/30" : ""}`}>
                {currentStatus}
                {asset.is_active_in_run && canEdit && <span className="ml-1.5 opacity-60">🔒</span>}
            </span>
            {asset.is_active_in_run && canEdit && (
                <div className={`absolute ${isHeader ? "top-full mt-2 left-0" : "bottom-full mb-1 left-1/2 -translate-x-1/2"} hidden group-hover/status:block w-48 p-2 bg-gray-900 text-white text-[10px] rounded shadow-xl z-[100] text-center leading-tight`}>
                    Locked during active runs
                </div>
            )}
        </div>
    );
};

const getPaginationRange = (currentPage: number, totalPages: number): (number | string)[] => {
    if (totalPages <= 5) {
        return Array.from({ length: totalPages }, (_, i) => i + 1);
    }

    const range: (number | string)[] = [];
    const showLeftEllipsis = currentPage > 3;
    const showRightEllipsis = currentPage < totalPages - 2;

    if (!showLeftEllipsis && showRightEllipsis) {
        for (let i = 1; i <= 4; i++) range.push(i);
        range.push('...');
        range.push(totalPages);
    } else if (showLeftEllipsis && !showRightEllipsis) {
        range.push(1);
        range.push('...');
        for (let i = totalPages - 3; i <= totalPages; i++) range.push(i);
    } else {
        range.push(1);
        range.push('...');
        range.push(currentPage - 1);
        range.push(currentPage);
        range.push(currentPage + 1);
        range.push('...');
        range.push(totalPages);
    }
    return range;
};

const AssetDetailPage: React.FC = () => {
    const { id, companyId } = useParams(); // companyId will be present for Admin/SubAdmin view
    const navigate = useNavigate();
    const location = useLocation();
    const [asset, setAsset] = useState<AssetData | null>(null);
    const [targets, setTargets] = useState<TargetData[]>([]);
    const [loading, setLoading] = useState(true);
    const [showWizard, setShowWizard] = useState(false);
    const [showResourceManager, setShowResourceManager] = useState(false);
    const [selectedTargetId, setSelectedTargetId] = useState<string | null>(null);
    const [activeTab, setActiveTab] = useState<"overview" | "target_list" | "endpoints" | "documentation" | "history">("overview");
    const [isSaving, setIsSaving] = useState(false);
    const [uploading, setUploading] = useState(false);
    const [error, setError] = useState("");
    const [searchQuery, setSearchQuery] = useState("");
    const [showEditModal, setShowEditModal] = useState(false);
    const [editingTarget, setEditingTarget] = useState<TargetData | null>(null);
    const [selectedEndpointTarget, setSelectedEndpointTarget] = useState<string>("");
    const [endpoints, setEndpoints] = useState<any[]>([]);
    const [endpointsLoading, setEndpointsLoading] = useState(false);
    const [activities, setActivities] = useState<ActivityLog[]>([]);
    const [activitiesLoading, setActivitiesLoading] = useState(false);
    const [showBulkImport, setShowBulkImport] = useState(false);
    const [editingEndpoint, setEditingEndpoint] = useState<any | null>(null);
    const [isSummaryExpanded, setIsSummaryExpanded] = useState(false);
    const [historyPage, setHistoryPage] = useState(1);
    const [targetPage, setTargetPage] = useState(1);
    const [endpointPage, setEndpointPage] = useState(1);
    const [endpointSearchQuery, setEndpointSearchQuery] = useState("");
    const [showTargetHint, setShowTargetHint] = useState(false);
    const [companyMembers, setCompanyMembers] = useState<{ username: string; display_name: string; pfp?: string }[]>([]);
    const [editingOwnership, setEditingOwnership] = useState(false);
    const [ownerDraft, setOwnerDraft] = useState({ risk_owner: "", technical_owner: "" });
    const [savingOwnership, setSavingOwnership] = useState(false);
    const [targetsLoading, setTargetsLoading] = useState(true);
    const { role: userRole, username } = useUserCredentials();

    // --- Pagination and Search Logic ---
    const PAGE_SIZE = 10;

    const filteredTargets = React.useMemo(() => {
        return targets.filter(t =>
            t.target_name.toLowerCase().includes(searchQuery.toLowerCase()) ||
            t.target_value.toLowerCase().includes(searchQuery.toLowerCase()) ||
            t.target_type.toLowerCase().includes(searchQuery.toLowerCase()) ||
            t.environment.toLowerCase().includes(searchQuery.toLowerCase())
        );
    }, [targets, searchQuery]);

    const paginatedTargets = React.useMemo(() => {
        const startIndex = (targetPage - 1) * PAGE_SIZE;
        return filteredTargets.slice(startIndex, startIndex + PAGE_SIZE);
    }, [filteredTargets, targetPage]);

    const totalTargetPages = Math.ceil(filteredTargets.length / PAGE_SIZE);

    const filteredEndpoints = React.useMemo(() => {
        return endpoints.filter(ep =>
            ep.value.toLowerCase().includes(endpointSearchQuery.toLowerCase()) ||
            (ep.notes && ep.notes.toLowerCase().includes(endpointSearchQuery.toLowerCase())) ||
            ep.resource_type.toLowerCase().includes(endpointSearchQuery.toLowerCase()) ||
            (ep.http_method && ep.http_method.toLowerCase().includes(endpointSearchQuery.toLowerCase()))
        );
    }, [endpoints, endpointSearchQuery]);

    const paginatedEndpoints = React.useMemo(() => {
        const startIndex = (endpointPage - 1) * PAGE_SIZE;
        return filteredEndpoints.slice(startIndex, startIndex + PAGE_SIZE);
    }, [filteredEndpoints, endpointPage]);

    const totalEndpointPages = Math.ceil(filteredEndpoints.length / PAGE_SIZE);

    // Reset pages when search changes
    useEffect(() => {
        setTargetPage(1);
    }, [searchQuery]);

    useEffect(() => {
        setEndpointPage(1);
    }, [endpointSearchQuery]);

    useEffect(() => {
        setEndpointPage(1);
    }, [selectedEndpointTarget]);

    const isCreatorRole =
        userRole === UserRole.ADMIN ||
        userRole === UserRole.SUB_ADMIN ||
        userRole === UserRole.BUSINESS_ADMINISTRATOR ||
        userRole === UserRole.BUSINESS; // backend further restricts to primary only

    const isOwner = asset != null && (asset.risk_owner === username || asset.technical_owner === username);

    // Can create a new asset from scratch
    const canCreate = isCreatorRole;
    // Can edit existing asset fields (creator roles + asset owners)
    const canEdit = isCreatorRole || isOwner;
    // Can change risk/technical owner (creator roles only)
    const canEditOwnership = isCreatorRole;

    // Form state for documentation
    const [docData, setDocData] = useState({
        architecture_diagram_url: [] as string[],
        product_documentation_url: [] as string[],
        runbook_url: [] as string[],
        threat_model_url: [] as string[],
        additional_notes: "",
        documentation_files: [] as { name: string; url: string; type: string; category?: string }[]
    });

    const [activeCategory, setActiveCategory] = useState<string | null>(null);


    useEffect(() => {
        if (asset) {
            setDocData({
                architecture_diagram_url: Array.isArray(asset.architecture_diagram_url) ? asset.architecture_diagram_url : (asset.architecture_diagram_url ? [asset.architecture_diagram_url] : []),
                product_documentation_url: Array.isArray(asset.product_documentation_url) ? asset.product_documentation_url : (asset.product_documentation_url ? [asset.product_documentation_url] : []),
                runbook_url: Array.isArray(asset.runbook_url) ? asset.runbook_url : (asset.runbook_url ? [asset.runbook_url] : []),
                threat_model_url: Array.isArray(asset.threat_model_url) ? asset.threat_model_url : (asset.threat_model_url ? [asset.threat_model_url] : []),
                additional_notes: asset.additional_notes || "",
                documentation_files: asset.documentation_files || []
            });
            setOwnerDraft({ risk_owner: asset.risk_owner || "", technical_owner: asset.technical_owner || "" });
        }
    }, [asset]);

    // Fetch company members for ownership dropdowns
    useEffect(() => {
        const targetCompanyId = companyId || asset?.company_id;
        const endpoint = targetCompanyId ? `/assets/members?company_id=${targetCompanyId}` : "/assets/members";
        axios.get(endpoint).then(res => {
            setCompanyMembers(res.data || []);
        }).catch(() => {});
    }, [companyId, asset?.company_id]);

    const handleSaveOwnership = async () => {
        if (!id) return;
        setSavingOwnership(true);
        try {
            const res = await axios.put(`/assets/${id}`, {
                risk_owner: ownerDraft.risk_owner,
                technical_owner: ownerDraft.technical_owner
            });
            setAsset(res.data);
            setEditingOwnership(false);
            toast.success("Ownership updated");
        } catch (err: any) {
            toast.error(err.response?.data?.message || "Failed to update ownership");
        } finally {
            setSavingOwnership(false);
        }
    };

    const handleStatusChange = async (assetId: string, newStatus: string) => {
        if (!asset) return;
        try {
            setAsset({ ...asset, status: newStatus });
            const promise = axios.put(`/assets/${assetId}/status`, { status: newStatus });
            
            toast.promise(promise, {
                loading: "Updating status...",
                success: `Asset is now ${newStatus}`,
                error: (err) => {
                    axios.get(`/assets/${assetId}`).then(res => setAsset(res.data));
                    return err.response?.data?.message || "Failed to update asset status";
                }
            });
            await promise;
        } catch (error) {
            console.error("Status update error", error);
        }
    };

    const renderValuesAsPills = (val: string | any[] | undefined, otherVal?: string, icon?: React.ReactNode) => {
        let values: string[] = [];
        if (Array.isArray(val)) {
            values = val;
        } else if (typeof val === "string") {
            if (val.startsWith("[")) {
                try {
                    const parsed = JSON.parse(val);
                    if (Array.isArray(parsed)) values = parsed;
                } catch (e) {
                    values = [val];
                }
            } else if (val.includes(",")) {
                values = val.split(",").map(v => v.trim());
            } else if (val) {
                if (val.length > 0 && val.length % 2 === 0 && /^[A-Z]+$/.test(val)) {
                    for (let i = 0; i < val.length; i += 2) {
                        values.push(val.substring(i, i + 2));
                    }
                } else {
                    values = [val];
                }
            }
        }

        if (values.length === 0 && !otherVal) return <span className="text-gray-400 font-normal italic">Not specified</span>;

        // Process "Other" value if present in the main values
        const finalValues = values.map(v => v === "Other" && otherVal ? otherVal : v);
        
        // If "Other" was selected but somehow not in the main list (shouldn't happen with current UI but good for safety)
        if (otherVal && !finalValues.includes(otherVal)) {
            // Check if "Other" is in the original list. If not, maybe just add it anyway if that's the intent.
            // But usually the UI adds "Other" to the array.
        }

        return (
            <div className="flex flex-wrap gap-2 mt-2">
                {finalValues.map((v, i) => (
                    <span key={i} className="inline-flex items-center px-2.5 py-1 bg-blue-50 text-blue-700 rounded-lg text-[12px] font-bold border border-blue-100/50 shadow-sm">
                        {icon && <span className="mr-1.5 opacity-60">{icon}</span>}
                        {v}
                    </span>
                ))}
            </div>
        );
    };

    const renderArray = (arr: any) => {
        if (Array.isArray(arr)) return arr.join(", ");
        if (typeof arr === "string" && arr.startsWith("[")) {
            try {
                const parsed = JSON.parse(arr);
                if (Array.isArray(parsed)) return parsed.join(", ");
            } catch (e) { }
        }
        return arr || "";
    };

    usePageTitle(asset ? `Asset: ${asset.name}` : "Asset Details");

    useEffect(() => {
        setHistoryPage(1);
        const fetchAsset = async () => {
            try {
                // If companyId is present, we might need to pass it, but usually the backend handles it via the ID.
                // However, for admin access to specific company, the asset ID should be enough if backend 'getAssetById' allows it.
                // We updated backend to allow access if ID is valid (and user is admin).
                const response = await axios.get(`/assets/${id}`);
                setAsset(response.data);
            } catch (err: any) {
                console.error("Error fetching asset:", err);
                setError("Failed to load asset details.");
            } finally {
                setLoading(false);
            }
        };

        const fetchTargets = async () => {
            try {
                setTargetsLoading(true);
                const response = await axios.get(`/assets/${id}/targets`);
                setTargets(response.data);
            } catch (err) {
                console.error("Error fetching targets:", err);
            } finally {
                setTargetsLoading(false);
            }
        };

        fetchAsset();
        fetchTargets();
    }, [id]);

    useEffect(() => {
        const queryParams = new URLSearchParams(location.search);
        if (queryParams.get("add-target") === "true") {
            setShowWizard(true);
            // Optionally clear the query param to avoid re-opening on manual refresh
            navigate(location.pathname, { replace: true });
        }
    }, [location.search, navigate, location.pathname]);

    useEffect(() => {
        const queryParams = new URLSearchParams(location.search);
        const tab = queryParams.get("tab");
        const validTabs: Array<"overview" | "target_list" | "endpoints" | "documentation" | "history"> = ["overview", "target_list", "endpoints", "documentation", "history"];
        if (tab && validTabs.includes(tab as any)) {
            setActiveTab(tab as any);
        }
    }, [location.search]);

    useEffect(() => {
        // Show target hint if no targets have been added yet and initial loading is complete
        if (!loading && !targetsLoading && targets.length === 0) {
            setShowTargetHint(true);
        } else {
            setShowTargetHint(false);
        }

        // If this was just created, clear the navigation state
        if (location.state?.isNew) {
            // Give a tiny delay to ensure everything is rendered before clearing state
            setTimeout(() => navigate(location.pathname, { replace: true, state: {} }), 100);
        }
    }, [location.state, targets.length, loading, targetsLoading, location.pathname, navigate]);

    const fetchEndpoints = useCallback(async (targetId: string) => {
        if (!targetId || !id) return;
        try {
            setEndpointsLoading(true);
            const { data } = await axios.get(`/assets/${id}/targets/${targetId}/resources`);
            setEndpoints(data);
        } catch (error) {
            console.error("Error fetching endpoints:", error);
            toast.error("Failed to fetch endpoints");
        } finally {
            setEndpointsLoading(false);
        }
    }, [id]);

    useEffect(() => {
        if (activeTab === "endpoints" && selectedEndpointTarget) {
            fetchEndpoints(selectedEndpointTarget);
        }
        // Redirect from history if user shouldn't see it
        if (activeTab === "history" && !canEdit) {
            setActiveTab("overview");
        }
    }, [activeTab, selectedEndpointTarget, fetchEndpoints, canEdit]);

    const fetchActivities = useCallback(async () => {
        if (!id) return;
        try {
            setActivitiesLoading(true);
            const { data } = await axios.get(`/assets/${id}/activity`);
            setActivities(data);
        } catch (error) {
            console.error("Error fetching activities:", error);
        } finally {
            setActivitiesLoading(false);
        }
    }, [id]);

    useEffect(() => {
        if (activeTab === "history") {
            fetchActivities();
        }
    }, [activeTab, fetchActivities]);

    const handleTargetRefresh = async () => {
        try {
            const response = await axios.get(`/assets/${id}/targets`);
            setTargets(response.data);
        } catch (err) {
            console.error("Error refreshing targets:", err);
        }
    };

    const handleTargetAdditionSuccess = () => {
        handleTargetRefresh();
        setActiveTab("target_list");
    };

    const handleDeleteTarget = async (targetId: string) => {
        if (!window.confirm("Are you sure you want to delete this target? This action cannot be undone.")) return;

        try {
            await axios.delete(`/assets/${id}/targets/${targetId}`);
            toast.success("Target deleted successfully");
            handleTargetRefresh();
        } catch (error: any) {
            console.error("Error deleting target:", error);
            toast.error(error.response?.data?.message || "Failed to delete target.");
        }
    };

    const handleEditTarget = (target: TargetData) => {
        setEditingTarget(target);
        setShowEditModal(true);
    };


    // Debounced save function for URLs and notes
    const debouncedSave = useRef(
        debounce(async (data: any) => {
            setIsSaving(true);
            try {
                const response = await axios.put(`/assets/${id}`, data);
                setAsset(response.data);
            } catch (err) {
                console.error("Auto-save failed:", err);
            } finally {
                setIsSaving(false);
            }
        }, 1000)
    ).current;

    const handleDocChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        const { name, value } = e.target;
        const newData = { ...docData, [name]: value };
        setDocData(newData);
        if (canEdit) {
            debouncedSave(newData);
        }
    };

    const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>, category?: string) => {
        const file = e.target.files?.[0];
        if (!file) return;

        if (file.size > 5 * 1024 * 1024) {
            alert("File size exceeds 5MB limit.");
            return;
        }

        setUploading(true);
        setActiveCategory(category || null);
        const formData = new FormData();
        formData.append("file", file);

        try {
            const response = await axios.post("/assets/upload", formData, {
                headers: { "Content-Type": "multipart/form-data" }
            });
            
            const newFile = { 
                name: file.name, 
                url: response.data.url, 
                type: file.type,
                category 
            };
            
            const newDocData = {
                ...docData,
                documentation_files: [...docData.documentation_files, newFile]
            };
            
            setDocData(newDocData);
            
            if (canEdit) {
                // Immediate save after upload
                await axios.put(`/assets/${id}`, newDocData);
                const assetRes = await axios.get(`/assets/${id}`);
                setAsset(assetRes.data);
            }
            
            toast.success("File uploaded successfully");
        } catch (err) {
            console.error("Error uploading file:", err);
            toast.error("Failed to upload file.");
        } finally {
            setUploading(false);
            setActiveCategory(null);
        }
    };

    const removeFile = async (index: number) => {
        const newFiles = docData.documentation_files.filter((_, i) => i !== index);
        const newDocData = { ...docData, documentation_files: newFiles };
        setDocData(newDocData);
        
        if (canEdit) {
            try {
                setIsSaving(true);
                await axios.put(`/assets/${id}`, newDocData);
                const assetRes = await axios.get(`/assets/${id}`);
                setAsset(assetRes.data);
                toast.success("File removed");
            } catch (err) {
                console.error("Failed to remove file:", err);
                toast.error("Failed to save changes after removal");
            } finally {
                setIsSaving(false);
            }
        }
    };

    const handleEndpointStatusChange = async (resourceId: number, newStatus: string) => {
        try {
            await axios.put(`/assets/${id}/targets/${selectedEndpointTarget}/resources/${resourceId}`, {
                status: newStatus
            });
            toast.success("Status updated");
            fetchEndpoints(selectedEndpointTarget);
            handleTargetRefresh();
        } catch (error: any) {
            toast.error(error.response?.data?.message || "Failed to update status");
        }
    };

    const handleDeleteEndpoint = async (resourceId: number) => {
        if (!window.confirm("Are you sure you want to delete this endpoint?")) return;
        try {
            await axios.delete(`/assets/${id}/targets/${selectedEndpointTarget}/resources/${resourceId}`);
            toast.success("Endpoint deleted");
            fetchEndpoints(selectedEndpointTarget);
            handleTargetRefresh();
        } catch (error: any) {
            toast.error(error.response?.data?.message || "Failed to delete endpoint");
        }
    };

    const handleUpdateEndpoint = async (resourceId: number, data: any) => {
        try {
            await axios.put(`/assets/${id}/targets/${selectedEndpointTarget}/resources/${resourceId}`, data);
            toast.success("Endpoint updated");
            setEditingEndpoint(null);
            fetchEndpoints(selectedEndpointTarget);
            handleTargetRefresh();
        } catch (error: any) {
            toast.error(error.response?.data?.message || "Failed to update endpoint");
        }
    };

    const handleAddLink = async (categoryKey: keyof typeof docData, link: string) => {
        if (!link || !link.startsWith('http')) {
            toast.error("Please enter a valid URL starting with http");
            return;
        }
        
        const currentLinks = (docData[categoryKey] as string[]) || [];
        if (currentLinks.includes(link)) {
            toast.error("Link already added");
            return;
        }

        const newDocData = {
            ...docData,
            [categoryKey]: [...currentLinks, link]
        };
        
        setDocData(newDocData);
        
        if (canEdit) {
            try {
                setIsSaving(true);
                await axios.put(`/assets/${id}`, newDocData);
                const assetRes = await axios.get(`/assets/${id}`);
                setAsset(assetRes.data);
                toast.success("Link added");
            } catch (err) {
                console.error("Failed to add link:", err);
                toast.error("Failed to save link");
            } finally {
                setIsSaving(false);
            }
        }
    };

    const handleRemoveLink = async (categoryKey: keyof typeof docData, linkToRemove: string) => {
        const currentLinks = (docData[categoryKey] as string[]) || [];
        const newDocData = {
            ...docData,
            [categoryKey]: currentLinks.filter(l => l !== linkToRemove)
        };
        
        setDocData(newDocData);
        
        if (canEdit) {
            try {
                setIsSaving(true);
                await axios.put(`/assets/${id}`, newDocData);
                const assetRes = await axios.get(`/assets/${id}`);
                setAsset(assetRes.data);
                toast.success("Link removed");
            } catch (err) {
                console.error("Failed to remove link:", err);
                toast.error("Failed to save changes");
            } finally {
                setIsSaving(false);
            }
        }
    };


    if (loading) {
        return (
            <div className="min-h-screen bg-gray-50 p-8 flex items-center justify-center">
                <div className="w-12 h-12 border-4 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
            </div>
        );
    }

    if (error || !asset) {
        return (
            <div className="min-h-screen bg-gray-50 p-8 flex flex-col items-center justify-center">
                <div className="bg-red-50 text-red-700 p-6 rounded-2xl border border-red-100 text-center max-w-md">
                    <FiInfo className="w-12 h-12 mx-auto mb-4 opacity-50" />
                    <h2 className="text-xl font-bold mb-2">Asset Not Found</h2>
                    <p className="mb-6">{error || "The asset you are looking for does not exist or you don't have access."}</p>
                    <button
                        onClick={() => navigate(companyId ? `/dashboard/companies/${companyId}#assets` : "/dashboard/assets")}
                        className="bg-red-600 text-white px-6 py-2 rounded-lg font-bold hover:bg-red-700 transition-colors"
                    >
                        Back to Inventory
                    </button>
                </div>
            </div>
        );
    }

    return (
        <motion.div
            className="min-h-screen bg-gray-50"
            variants={containerVariants}
            initial="hidden"
            animate="visible"
        >
            <div className="bg-[#1a1f37] px-6 py-4 my-6 mx-6 rounded-lg">
                <div className="max-w-[1600px] mx-auto flex items-center justify-between">
                    <div className="flex items-center gap-4">
                        <button
                            onClick={() => navigate(companyId ? `/dashboard/companies/${companyId}#assets` : "/dashboard/assets")}
                            className="p-2 text-white hover:bg-white/10 rounded-lg transition-all"
                        >
                            <FiArrowLeft className="w-5 h-5" />
                        </button>
                    <div className="flex items-center gap-3">
                            <h1 className="text-xl font-bold text-white">{asset.name}</h1>
                            <StatusBadge asset={asset} canEdit={canEdit} onStatusChange={handleStatusChange} isHeader={true} />
                        </div>
                    </div>
                    {canEdit && (
                        <div className="flex gap-3">
                            <button
                                onClick={() => navigate(`/dashboard/assets/create/${asset.id}`)}
                                className="px-4 py-2 bg-white text-gray-700 rounded-lg text-sm font-semibold hover:bg-gray-100 transition-colors flex items-center gap-2"
                            >
                                <FiEdit2 className="w-4 h-4" />
                                Edit Asset
                            </button>
                            <div className="relative">
                                <button
                                    onClick={() => setShowWizard(true)}
                                    className={`px-5 py-2.5 bg-[#10b981] text-white rounded-xl text-sm font-bold transition-all flex items-center gap-2 shadow-xl shadow-green-500/20 hover:bg-[#059669] active:scale-95 ${showTargetHint ? 'ring-4 ring-green-500/30 ring-offset-0 animate-pulse' : ''}`}
                                >
                                    <FiPlus className="w-4 h-4" />
                                    Add Target
                                </button>
                                
                                <AnimatePresence>
                                    {showTargetHint && (
                                        <motion.div 
                                            initial={{ opacity: 0, y: 15, scale: 0.9 }}
                                            animate={{ opacity: 1, y: 0, scale: 1 }}
                                            exit={{ opacity: 0, y: 15, scale: 0.9 }}
                                            className="absolute top-full right-0 mt-5 w-80 p-7 bg-gradient-to-br from-[#1a1f37] to-[#0f172a] text-white rounded-[32px] shadow-[0_25px_60px_rgba(0,0,0,0.5)] z-50 border border-white/10"
                                        >
                                            <div className="flex items-center gap-3 mb-3">
                                                <div className="w-2.5 h-2.5 rounded-full bg-green-500 shadow-[0_0_15px_rgba(34,197,94,0.7)] animate-pulse" />
                                                <span className="text-[10px] font-black text-green-400 uppercase tracking-[0.25em]">Guided Onboarding</span>
                                            </div>
                                            <h4 className="text-[17px] font-black mb-2 tracking-tight">Your Inventory is ready!</h4>
                                            <p className="text-[13px] text-gray-400 font-medium leading-relaxed">
                                                Great start! Every asset needs at least one <b>Target</b> (like a Domain, IP, or URL) to be testable. Click here to add your first one.
                                            </p>
                                            
                                            {/* Precision Arrow pointing back to button center */}
                                            <div className="absolute -top-2 right-[60px] w-4 h-4 bg-[#1a1f37] rotate-45 border-l border-t border-white/10" />
                                        </motion.div>
                                    )}
                                </AnimatePresence>
                            </div>
                        </div>
                    )}
                </div>
            </div>

            {/* Main Layout Grid */}
            <div className="max-w-[1600px] mx-auto px-6 pb-8">
                <div className="grid grid-cols-12 gap-6">

                    {/* Left Sidebar - Always Visible */}
                    <div className="col-span-12 lg:col-span-4 space-y-6">
                        {/* Asset Identity Card - Premium Redesign */}
                        <div className="bg-white rounded-[24px] shadow-sm border border-gray-100">
                            {/* Dark Navy Header Overlap */}
                            <div className="bg-[#1a1f37]  h-24 relative" />

                            <div className="px-7 pb-6 -mt-12 relative z-10">
                                {/* Overlapping Icon Box */}
                                <div className="w-20 h-20 bg-white rounded-2xl shadow-xl flex items-center justify-center border border-gray-50 mb-4">
                                    <FiShield className="w-9 h-9 text-blue-600" />
                                </div>

                                <h2 className="text-[18px] font-bold text-[#0f172a] mb-0.5 tracking-tight">{asset.name}</h2>
                                <div className="relative mb-5 group/summary">
                                    <ReadMoreText
                                        text={asset.summary || "No description provided"}
                                        maxLines={4}
                                        className="text-[13px] text-[#94a3b8] font-medium"
                                    />
                                </div>

                                {/* Badges Section */}
                                <div className="flex flex-wrap gap-2 mb-5">
                                    <span className={`px-4 py-1 rounded-xl text-[11px] font-bold border flex items-center gap-1.5 ${asset.criticality === "Critical" ? "bg-red-50 text-red-600 border-red-100" :
                                        asset.criticality === "High" ? "bg-orange-50 text-orange-600 border-orange-100" :
                                            "bg-amber-50 text-amber-600 border-amber-100"
                                        }`}>
                                        {asset.criticality || "Medium"}
                                    </span>
                                    <StatusBadge asset={asset} canEdit={canEdit} onStatusChange={handleStatusChange} isHeader={false} />
                                </div>

                                {/* Separator */}
                                <div className="border-t border-gray-100 mb-5" />

                                {/* Info Grid */}
                                <div className="grid grid-cols-2 gap-x-4 gap-y-3">
                                    {/* Category */}
                                    <div className="flex flex-col">
                                        <div className="flex items-center gap-2 text-[#94a3b8] mb-2">
                                            <FiFileText className="w-3.5 h-3.5" />
                                            <span className="text-[12px] font-bold uppercase tracking-wider">Category</span>
                                        </div>
                                        <p className="text-[14px] text-[#334155] font-bold truncate">
                                            {asset.category === "Other" ? asset.category_other : asset.category}
                                        </p>
                                    </div>

                                    {/* Lifecycle */}
                                    <div className="flex flex-col">
                                        <div className="flex items-center gap-2 text-[#94a3b8] mb-2">
                                            <FiActivity className="w-3.5 h-3.5" />
                                            <span className="text-[12px] font-bold uppercase tracking-wider">Lifecycle</span>
                                        </div>
                                        <p className="text-[14px] text-[#334155] font-bold">
                                            {asset.lifecycle_status}
                                        </p>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Ownership */}
                        <InfoCard title="Ownership" icon={<FiUsers className="w-4 h-4" />} iconColor="text-blue-600">
                            <div className="space-y-5">
                                {editingOwnership ? (
                                    <>
                                        {/* Risk Owner Dropdown */}
                                        <div>
                                            <p className="text-[11px] font-bold text-gray-400 uppercase tracking-wider mb-2">Risk Owner</p>
                                            <select
                                                value={ownerDraft.risk_owner}
                                                onChange={e => setOwnerDraft(d => ({ ...d, risk_owner: e.target.value }))}
                                                className="w-full px-3 py-2 border border-gray-200 rounded-xl text-sm font-semibold text-gray-900 bg-white outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all"
                                            >
                                                <option value="">Select a member...</option>
                                                {companyMembers.map(m => (
                                                    <option key={m.username} value={m.username}>
                                                        {m.display_name} (@{m.username})
                                                    </option>
                                                ))}
                                            </select>
                                        </div>
                                        {/* Technical Owner Dropdown */}
                                        <div>
                                            <p className="text-[11px] font-bold text-gray-400 uppercase tracking-wider mb-2">Technical Owner</p>
                                            <select
                                                value={ownerDraft.technical_owner}
                                                onChange={e => setOwnerDraft(d => ({ ...d, technical_owner: e.target.value }))}
                                                className="w-full px-3 py-2 border border-gray-200 rounded-xl text-sm font-semibold text-gray-900 bg-white outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all"
                                            >
                                                <option value="">Select a member...</option>
                                                {companyMembers.map(m => (
                                                    <option key={m.username} value={m.username}>
                                                        {m.display_name} (@{m.username})
                                                    </option>
                                                ))}
                                            </select>
                                        </div>
                                        {/* Actions */}
                                        <div className="flex gap-2 pt-1">
                                            <button
                                                onClick={handleSaveOwnership}
                                                disabled={savingOwnership}
                                                className="flex-1 flex items-center justify-center gap-1.5 px-4 py-2 bg-[#1a1f37] hover:bg-[#252c4a] disabled:opacity-50 text-white text-xs font-bold rounded-xl transition-all"
                                            >
                                                <FiCheck className="w-3.5 h-3.5" />
                                                {savingOwnership ? "Saving..." : "Save"}
                                            </button>
                                            <button
                                                onClick={() => { setEditingOwnership(false); setOwnerDraft({ risk_owner: asset?.risk_owner || "", technical_owner: asset?.technical_owner || "" }); }}
                                                className="px-4 py-2 text-gray-500 hover:bg-gray-100 text-xs font-bold rounded-xl transition-all border border-gray-200"
                                            >
                                                Cancel
                                            </button>
                                        </div>
                                    </>
                                ) : (
                                    <>
                                        {/* Risk Owner display */}
                                        <div className="flex items-center gap-3">
                                            <div className="w-10 h-10 bg-blue-500 rounded-full flex items-center justify-center text-white font-semibold text-sm">
                                                {asset.risk_owner?.[0]?.toUpperCase()}
                                            </div>
                                            <div className="flex-1">
                                                <p className="text-xs text-gray-500 mb-1">Risk Owner</p>
                                                <p className="text-sm font-semibold text-gray-900">{asset.risk_owner || "Not assigned"}</p>
                                            </div>
                                        </div>
                                        {/* Technical Owner display */}
                                        <div className="flex items-center gap-3">
                                            <div className="w-10 h-10 bg-gray-100 rounded-full flex items-center justify-center text-gray-500 font-semibold text-sm border border-dashed border-gray-300">
                                                {asset.technical_owner_invite_later ? <FiUserPlus className="w-4 h-4" /> : (asset.technical_owner ? asset.technical_owner[0].toUpperCase() : '?')}
                                            </div>
                                            <div className="flex-1">
                                                <p className="text-xs text-gray-500 mb-1">Technical Owner</p>
                                                {asset.technical_owner_invite_later ? (
                                                    <div>
                                                        <div className="flex items-center gap-2">
                                                            <p className="text-sm font-semibold text-gray-900">To be invited</p>
                                                            <button 
                                                                onClick={() => navigate(companyId ? `/dashboard/companies/${companyId}#team` : "/dashboard/settings")} 
                                                                className="text-xs px-2 py-0.5 bg-blue-50 text-blue-600 rounded hover:bg-blue-100 font-medium transition-colors border border-blue-100"
                                                            >
                                                                Invite Team Member
                                                            </button>
                                                        </div>
                                                        <p className="text-[10px] text-gray-500 mt-1 italic">
                                                            Note: Only Business Owners and Admins can invite new users.
                                                        </p>
                                                    </div>
                                                ) : (
                                                    <p className="text-sm font-semibold text-gray-900">
                                                        {asset.technical_owner || "Not assigned"}
                                                    </p>
                                                )}
                                            </div>
                                        </div>
                                        {/* Edit button — only for canEditOwnership */}
                                        {canEditOwnership && (
                                            <button
                                                onClick={() => setEditingOwnership(true)}
                                                className="w-full flex items-center justify-center gap-2 px-4 py-2.5 border border-gray-200 hover:bg-gray-50 text-gray-600 hover:text-gray-900 text-xs font-bold rounded-xl transition-all"
                                            >
                                                <FiEdit2 className="w-3.5 h-3.5" />
                                                Edit Ownership
                                            </button>
                                        )}
                                    </>
                                )}
                            </div>
                        </InfoCard>

                        {/* Register History - Only for Admins, BusAdmins, BusOwners, Risk/Tech Owners */}
                        {canEdit && (
                            <InfoCard title="Register History" icon={<FiClock className="w-4 h-4" />} iconColor="text-gray-600">
                                <div className="space-y-6">
                                    <div className="flex items-center gap-4">
                                        <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                                        <div className="flex-1">
                                            <p className="text-xs font-semibold text-gray-900 mb-1.5">Asset Registered</p>
                                            <p className="text-xs text-gray-500">{format(new Date(asset.created_at), "MMMM dd, yyyy")}</p>
                                        </div>
                                    </div>
                                    <div className="flex items-center gap-4">
                                        <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                                        <div className="flex-1">
                                            <p className="text-xs font-semibold text-gray-900 mb-1.5">Last Verified</p>
                                            <p className="text-xs text-gray-500">{format(new Date(asset.updated_at), "MMMM dd, yyyy • HH:mm")}</p>
                                        </div>
                                    </div>
                                </div>
                            </InfoCard>
                        )}

                    </div>

                    {/* Right Content Area */}
                    <div className="col-span-12 lg:col-span-8 space-y-6">

                        {/* Tab Navigation */}
                        {/* Tab Navigation */}
                        <div className="flex items-center gap-3 mb-6">
                            <button
                                onClick={() => setActiveTab("overview")}
                                className={`px-4 py-2.5 text-xs font-bold flex items-center gap-1.5 transition-all rounded-lg border whitespace-nowrap ${activeTab === "overview" ? "bg-[#1a1f37] text-white border-[#1a1f37] shadow-md" : "bg-white text-gray-600 border-gray-200 hover:bg-gray-50 shadow-sm"}`}
                            >
                                <FiInfo className={`w-3.5 h-3.5 ${activeTab === "overview" ? "text-white" : "text-gray-500"}`} />
                                Overview
                            </button>
                            <button
                                onClick={() => setActiveTab("target_list")}
                                className={`px-4 py-2.5 text-xs font-bold flex items-center gap-1.5 transition-all rounded-lg border whitespace-nowrap ${activeTab === "target_list" ? "bg-[#1a1f37] text-white border-[#1a1f37] shadow-md" : "bg-white text-gray-600 border-gray-200 hover:bg-gray-50 shadow-sm"}`}
                            >
                                <FiTarget className={`w-3.5 h-3.5 ${activeTab === "target_list" ? "text-white" : "text-gray-500"}`} />
                                Target List
                                <span className={`ml-1 px-1.5 py-0.5 rounded-md text-xs ${activeTab === "target_list" ? "bg-white/10 text-white" : "bg-gray-100 text-gray-600"}`}>{targets.length}</span>
                            </button>
                            <button
                                onClick={() => setActiveTab("endpoints")}
                                className={`px-4 py-2.5 text-xs font-bold flex items-center gap-1.5 transition-all rounded-lg border whitespace-nowrap ${activeTab === "endpoints" ? "bg-[#1a1f37] text-white border-[#1a1f37] shadow-md" : "bg-white text-gray-600 border-gray-200 hover:bg-gray-50 shadow-sm"}`}
                            >
                                <FiLink className={`w-3.5 h-3.5 ${activeTab === "endpoints" ? "text-white" : "text-gray-500"}`} />
                                Endpoints
                                <span className={`ml-1 px-1.5 py-0.5 rounded-md text-xs ${activeTab === "endpoints" ? "bg-white/10 text-white" : "bg-gray-100 text-gray-600"}`}>{targets.reduce((acc, t) => acc + (t.resource_count || 0), 0)}</span>
                            </button>
                            <button
                                onClick={() => setActiveTab("documentation")}
                                className={`px-4 py-2.5 text-xs font-bold flex items-center gap-1.5 transition-all rounded-lg border whitespace-nowrap ${activeTab === "documentation" ? "bg-[#1a1f37] text-white border-[#1a1f37] shadow-md" : "bg-white text-gray-600 border-gray-200 hover:bg-gray-50 shadow-sm"}`}
                            >
                                <FiFileText className={`w-3.5 h-3.5 ${activeTab === "documentation" ? "text-white" : "text-gray-500"}`} />
                                Documentation
                            </button>
                            {canEdit && (
                                <button
                                    onClick={() => setActiveTab("history")}
                                    className={`px-4 py-2.5 text-xs font-bold flex items-center gap-1.5 transition-all rounded-lg border whitespace-nowrap ${activeTab === "history" ? "bg-[#1a1f37] text-white border-[#1a1f37] shadow-md" : "bg-white text-gray-600 border-gray-200 hover:bg-gray-50 shadow-sm"}`}
                                >
                                    <FiActivity className={`w-3.5 h-3.5 ${activeTab === "history" ? "text-white" : "text-gray-500"}`} />
                                    Register History
                                </button>
                            )}
                        </div>





                        {/* Tab Contents */}
                        {activeTab === "overview" && (
                            <div className="space-y-10 animate-in fade-in slide-in-from-bottom-4 duration-500">

                                {/* Asset Identity & Context */}
                                <InfoCard title="Asset Identity & Context" icon={<FiInfo />}>
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-x-16 gap-y-10">
                                        <div>
                                            <h4 className="text-[13px] font-bold text-[#94a3b8] tracking-wider mb-6">General Information</h4>
                                            <DataRow label="Asset Name" value={asset.name} />
                                            <DataRow label="Asset Category" value={asset.category === "Other" ? asset.category_other : asset.category} />
                                            <DataRow label="Primary Types" value={renderValuesAsPills(asset.primary_type, asset.primary_type_other)} />
                                        </div>
                                        <div>
                                            <h4 className="text-[13px] font-bold text-[#94a3b8] tracking-wider mb-6">Technical Scope</h4>
                                            <DataRow label="Included Components" value={renderValuesAsPills(asset.includes, asset.includes_other)} />
                                        </div>
                                    </div>
                                </InfoCard>

                                {/* Technical Lifecycle */}
                                <InfoCard title="Technical Lifecycle" icon={<FiActivity />}>
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-x-16 gap-y-10">
                                        <div>
                                            <DataRow label="Lifecycle Status" value={asset.lifecycle_status === "Other" ? asset.lifecycle_status_other : asset.lifecycle_status} />
                                            <DataRow label="Change Frequency" value={asset.change_frequency === "Other" ? asset.change_frequency_other : asset.change_frequency} />
                                        </div>
                                        <div>
                                            <DataRow label="Deployment Model" value={asset.deployment_model === "Other" ? asset.deployment_model_other : asset.deployment_model} />
                                            <DataRow label="Target Audience" value={renderValuesAsPills(asset.primary_audience)} />
                                        </div>
                                    </div>
                                </InfoCard>

                                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                    {/* Data & Privacy */}
                                    <InfoCard title="Data & Privacy" icon={<FiDatabase />}>
                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-16 gap-y-10">
                                            <div>
                                                <DataRow label="Data Classification" value={renderValuesAsPills(asset.data_classification === "Other" ? asset.data_classification_other : asset.data_classification)} />
                                                <DataRow label="Data Residency" value={renderValuesAsPills(asset.data_residency, undefined, <FiGlobe className="w-3.5 h-3.5" />)} />
                                            </div>
                                            <div>
                                                <DataRow label="Sensitive Data Description" value={<ReadMoreText text={asset.sensitive_data_description || "None provided"} maxLines={4} />} />
                                                <DataRow label="Compliance Frameworks" value={renderValuesAsPills(asset.compliance_frameworks, asset.compliance_other)} />
                                            </div>
                                        </div>
                                    </InfoCard>

                                    {/* Access & Identity */}
                                    <InfoCard title="Access & Identity" icon={<FiLock />}>
                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-16 gap-y-10">
                                            <div>
                                                <DataRow label="Auth Required" value={asset.authentication_required} />
                                                <DataRow label="Authorization Model" value={renderValuesAsPills(asset.authorization_model, asset.authorization_model_other)} />
                                            </div>
                                            <div>
                                                <DataRow label="Identity Provider" value={<ReadMoreText text={asset.identity_provider} />} />
                                                <DataRow label="User Roles" value={<ReadMoreText text={asset.user_roles} />} />
                                            </div>
                                        </div>
                                    </InfoCard>

                                    {/* Security Profile */}
                                    <InfoCard title="Security Profile" icon={<FiShield />}>
                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-16 gap-y-10">
                                            <div>
                                                <DataRow label="Internet Exposure" value={
                                                    <span className={`inline-flex items-center gap-2 px-3.5 py-1.5 rounded-xl text-[13px] font-bold ${asset.internet_exposure === 'Restricted' ? 'bg-orange-50 text-orange-700' : 'bg-gray-50 text-gray-700'}`}>
                                                        <FiAlertTriangle className="w-4 h-4" />
                                                        {asset.internet_exposure}
                                                    </span>
                                                } />

                                            </div>
                                            <div>
                                                <DataRow label="Allowed Testing" value={renderValuesAsPills(asset.allowed_testing_types)} />
                                                <DataRow label="Testing Constraints" value={<ReadMoreText text={asset.testing_constraints || "None"} />} />
                                            </div>
                                        </div>
                                    </InfoCard>

                                    {/* Governance & Compliance */}
                                    <InfoCard title="Governance & Compliance" icon={<FiCheckCircle />}>
                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-16 gap-y-10">
                                            <div>
                                                <DataRow label="Legal Ownership" value={
                                                    <div className="flex items-center gap-2.5">
                                                        <FiCheckCircle className={`w-5 h-5 ${asset.legal_ownership_confirmed ? 'text-[#10b981]' : 'text-gray-300'}`} />
                                                        <span>{asset.legal_ownership_confirmed ? "Confirmed" : "Not Confirmed"}</span>
                                                    </div>
                                                } />
                                                <DataRow label="Approved By" value={asset.approved_by || "Pending Approval"} />
                                            </div>
                                            <div>
                                                <DataRow label="Approval Date" value={
                                                    asset.approval_date ? (
                                                        <div className="flex items-center gap-2.5">
                                                            <FiClock className="w-5 h-5 text-gray-400" />
                                                            {format(new Date(asset.approval_date), "MMM dd, yyyy")}
                                                        </div>
                                                    ) : "N/A"
                                                } />
                                            </div>
                                        </div>
                                    </InfoCard>
                                </div>
                            </div>
                        )}

                        {activeTab === "target_list" && (
                            <div className="space-y-6 animate-in fade-in slide-in-from-bottom-4 duration-500">
                                {/* Search Filter */}
                                <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-2">
                                    <div className="flex-1 w-full max-w-md relative group">
                                        <div className="flex items-center gap-3 bg-white p-2.5 px-3.5 rounded-xl border border-gray-100 shadow-sm transition-all focus-within:border-blue-300 focus-within:ring-4 focus-within:ring-blue-500/5">
                                            <div className="text-slate-400 pl-0.5">
                                                <FiSearch className="w-4 h-4" />
                                            </div>
                                            <input
                                                type="text"
                                                className="flex-1 bg-transparent border-none text-sm font-semibold text-slate-700 placeholder:text-slate-400 focus:ring-0 outline-none"
                                                placeholder="Search by name, value, or type..."
                                                value={searchQuery}
                                                onChange={(e) => setSearchQuery(e.target.value)}
                                            />
                                            {searchQuery && (
                                                <button
                                                    onClick={() => setSearchQuery("")}
                                                    className="p-1 text-slate-400 hover:text-slate-600 rounded-lg hover:bg-slate-50 transition-colors"
                                                >
                                                    <FiX className="w-3.5 h-3.5" />
                                                </button>
                                            )}
                                        </div>
                                    </div>
                                    <div className="flex items-center gap-3">
                                        <div className="flex items-baseline gap-1.5">
                                            <span className="text-[10px] font-black text-slate-300 uppercase tracking-[0.15em]">Showing</span>
                                            <span className="text-xs font-black text-blue-600 tracking-tight">{paginatedTargets.length}</span>
                                            <span className="text-[10px] font-black text-slate-300 uppercase tracking-[0.15em]">of</span>
                                            <span className="text-xs font-black text-slate-700 tracking-tight">{filteredTargets.length}</span>
                                        </div>
                                    </div>
                                </div>

                                {/* Target Table */}
                                <div className="bg-white rounded-lg shadow-sm border border-gray-100 overflow-hidden">
                                    <table className="w-full">
                                        <thead className="bg-gray-50 border-b border-gray-100">
                                            <tr>
                                                <th className="px-6 py-4 text-left text-xs font-bold text-gray-500 uppercase tracking-wider">Target</th>
                                                <th className="px-6 py-4 text-left text-xs font-bold text-gray-500 uppercase tracking-wider">Environment</th>
                                                <th className="px-6 py-4 text-left text-xs font-bold text-gray-500 uppercase tracking-wider">Status</th>
                                                <th className="px-6 py-4 text-right text-xs font-bold text-gray-500 uppercase tracking-wider">Actions</th>
                                            </tr>
                                        </thead>
                                        <tbody className="divide-y divide-gray-100">
                                            {filteredTargets.length === 0 ? (
                                                <tr>
                                                    <td colSpan={4} className="px-6 py-12 text-center text-gray-500">
                                                        <FiTarget className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                                                        <p>{searchQuery ? "No targets match your search." : "No targets found for this asset."}</p>
                                                    </td>
                                                </tr>
                                            ) : (
                                                paginatedTargets.map((target, idx) => (
                                                    <tr key={target.target_id || idx} className="hover:bg-gray-50 transition-colors">
                                                        <td className="px-6 py-4">
                                                            <div className="flex items-center gap-3">
                                                                <div className="p-2 rounded-lg bg-gray-100 text-gray-600">
                                                                    {target.target_type === 'API' ? <FiDatabase className="w-4 h-4" /> :
                                                                        target.target_type === 'Mobile' ? <FiTarget className="w-4 h-4" /> :
                                                                            <FiGlobe className="w-4 h-4" />}
                                                                </div>
                                                                <div>
                                                                    <p className="text-sm font-bold text-gray-900">{target.target_name}</p>
                                                                    <div className="flex items-center gap-2">
                                                                        {(!(target.target_value === "Uploaded Binary" || target.target_value === target.metadata?.binary_name) || !target.metadata?.binary_url) && (
                                                                            <p className="text-xs text-gray-400">{target.target_value}</p>
                                                                        )}
                                                                        {target.metadata?.binary_url && (
                                                                            <a
                                                                                href={target.metadata.binary_url}
                                                                                target="_blank"
                                                                                rel="noopener noreferrer"
                                                                                className="flex items-center gap-1 text-[10px] font-bold text-blue-600 hover:text-blue-700 bg-blue-50 px-1.5 py-0.5 rounded transition-colors"
                                                                                onClick={(e) => e.stopPropagation()}
                                                                                title={target.metadata.binary_name || "Download File"}
                                                                            >
                                                                                <FiDownload className="w-2.5 h-2.5" /> {target.metadata.binary_name || "File"}
                                                                            </a>
                                                                        )}
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        </td>
                                                        <td className="px-6 py-4">
                                                            <span className={`px-2 py-1 rounded text-xs font-bold ${target.environment === 'Production' ? 'bg-red-50 text-red-600' : 'bg-yellow-50 text-yellow-600'}`}>
                                                                {target.environment}
                                                            </span>
                                                        </td>
                                                        <td className="px-6 py-4">
                                                            <span className={`inline-flex items-center gap-1.5 px-2 py-1 rounded-full text-xs font-bold ${target.status === 'active' || target.status === 'Active' ? 'bg-green-50 text-green-600' : 'bg-gray-100 text-gray-500'}`}>
                                                                <span className={`w-1.5 h-1.5 rounded-full ${target.status === 'active' || target.status === 'Active' ? 'bg-green-500' : 'bg-gray-400'}`}></span>
                                                                {target.status}
                                                            </span>
                                                        </td>
                                                        <td className="px-6 py-4 text-right">
                                                            <div className="flex items-center justify-end gap-2">
                                                                {target.is_linked ? (
                                                                    <div className="flex items-center gap-1 px-2 py-1 rounded bg-orange-50 text-orange-600 text-[10px] font-bold" title="Linked to program - cannot be edited or deleted">
                                                                        <FiLock className="w-3 h-3" /> Linked
                                                                    </div>
                                                                ) : canEdit ? (
                                                                    <>
                                                                        <button
                                                                            onClick={() => handleEditTarget(target)}
                                                                            className="p-1.5 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                                                                            title="Edit Target"
                                                                        >
                                                                            <FiEdit2 className="w-3.5 h-3.5" />
                                                                        </button>
                                                                        <button
                                                                            onClick={() => handleDeleteTarget(target.target_id)}
                                                                            className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                                                                            title="Delete Target"
                                                                        >
                                                                            <FiTrash2 className="w-3.5 h-3.5" />
                                                                        </button>
                                                                    </>
                                                                ) : (
                                                                    <div className="text-[10px] text-gray-400 font-bold uppercase tracking-widest">
                                                                        View Only
                                                                    </div>
                                                                )}
                                                            </div>
                                                        </td>
                                                    </tr>
                                                ))
                                            )}
                                        </tbody>
                                    </table>
                                    {/* Pagination Footer */}
                                    {totalTargetPages > 1 && (
                                        <div className="px-6 py-4 bg-transparent border-t border-gray-100 flex items-center justify-between">
                                            <div className="flex items-baseline gap-1.5">
                                                <span className="text-[10px] font-black text-slate-300 uppercase tracking-[0.15em]">Showing</span>
                                                <span className="text-xs font-black text-blue-600 tracking-tight">{(targetPage - 1) * PAGE_SIZE + 1}</span>
                                                <span className="text-[10px] font-black text-slate-300 uppercase tracking-[0.15em]">to</span>
                                                <span className="text-xs font-black text-blue-600 tracking-tight">{Math.min(targetPage * PAGE_SIZE, filteredTargets.length)}</span>
                                                <span className="text-[10px] font-black text-slate-300 uppercase tracking-[0.15em]">of</span>
                                                <span className="text-xs font-black text-slate-700 tracking-tight">{filteredTargets.length}</span>
                                            </div>
                                            <div className="flex items-center gap-2">
                                                <button
                                                    onClick={() => setTargetPage(p => Math.max(1, p - 1))}
                                                    disabled={targetPage === 1}
                                                    className="p-1.5 rounded-lg border border-gray-200 bg-white text-gray-400 hover:text-blue-600 hover:border-blue-100 disabled:opacity-30 transition-all font-bold shadow-sm"
                                                >
                                                    <FiChevronLeft className="w-4 h-4" />
                                                </button>
                                                <div className="flex items-center gap-1">
                                                    {getPaginationRange(targetPage, totalTargetPages).map((item, i) => {
                                                        if (item === "...") {
                                                            return <span key={`ellipsis-${i}`} className="text-slate-300 px-1 font-bold">...</span>;
                                                        }
                                                        const pageNum = item as number;
                                                        return (
                                                            <button
                                                                key={pageNum}
                                                                onClick={() => setTargetPage(pageNum)}
                                                                className={`w-8 h-8 rounded-lg text-xs font-bold transition-all ${targetPage === pageNum ? 'bg-blue-600 text-white shadow-md' : 'bg-white text-gray-500 border border-gray-100 hover:border-blue-100 hover:text-blue-600'}`}
                                                            >
                                                                {pageNum}
                                                            </button>
                                                        );
                                                    })}
                                                </div>
                                                <button
                                                    onClick={() => setTargetPage(p => Math.min(totalTargetPages, p + 1))}
                                                    disabled={targetPage === totalTargetPages}
                                                    className="p-1.5 rounded-lg border border-gray-200 bg-white text-gray-400 hover:text-blue-600 hover:border-blue-100 disabled:opacity-30 transition-all font-bold shadow-sm"
                                                >
                                                    <FiChevronRight className="w-4 h-4" />
                                                </button>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>
                        )}

                        {activeTab === "endpoints" && (
                            <div className="space-y-6 animate-in fade-in slide-in-from-bottom-4 duration-500">
                                {/* Endpoint Management Header - Redesigned for Creative & Robust Layout */}
                                <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
                                    <div className="flex flex-col xl:flex-row items-stretch">
                                        {/* Left: Identity Section (Creative & Always Visible) */}
                                        <div className="bg-gray-50/50 p-5 px-6 border-b xl:border-b-0 xl:border-r border-gray-100 flex items-center gap-4 min-w-max">
                                            <div className="p-3 bg-blue-600 text-white rounded-xl shadow-lg shadow-blue-500/20 shrink-0">
                                                <FiLink className="w-5 h-5" />
                                            </div>
                                            <div className="flex flex-col">
                                                <h3 className="text-lg font-extrabold text-[#1a1f37] whitespace-nowrap leading-tight">Endpoints</h3>
                                                <p className="text-gray-400 text-[10px] font-bold uppercase tracking-widest mt-0.5 whitespace-nowrap opacity-80">
                                                    {asset?.category === 'Digital Application' || asset?.category === 'Mobile App'
                                                        ? 'URI Paths & Routing'
                                                        : asset?.category === 'Infrastructure'
                                                            ? 'IP Addresses & Nodes'
                                                            : asset?.category === 'Cloud'
                                                                ? 'Resource IDs & Access'
                                                                : 'Target Governance'}
                                                </p>
                                            </div>
                                        </div>

                                        {/* Right: Action Section (Adaptive & Balanced) */}
                                        <div className="flex-1 p-5 px-6 flex flex-col md:flex-row items-center justify-between gap-6">
                                            <div className="flex-1 w-full max-w-full md:max-w-2xl min-w-0">
                                                <div className="relative group">
                                                    <div className="absolute left-3.5 top-1/2 -translate-y-1/2 text-blue-500/40 group-focus-within:text-blue-500 transition-colors pointer-events-none">
                                                        <FiTarget className="w-4 h-4" />
                                                    </div>
                                                    <select
                                                        value={selectedEndpointTarget}
                                                        onChange={(e) => setSelectedEndpointTarget(e.target.value)}
                                                        className="appearance-none bg-white border border-gray-200 text-gray-900 text-sm font-semibold rounded-xl pl-10 pr-10 py-2.5 outline-none focus:ring-4 focus:ring-blue-500/5 focus:border-blue-500 transition-all w-full cursor-pointer hover:border-blue-200 shadow-sm"
                                                    >
                                                        <option value="" className="text-slate-900">Select target surface to manage endpoints...</option>
                                                        {targets.map(t => (
                                                            <option key={t.target_id} value={t.target_id} className="text-slate-900">
                                                                {t.target_name} ({t.target_value})
                                                            </option>
                                                        ))}
                                                    </select>
                                                    <FiChevronDown className="absolute right-3.5 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none w-4 h-4" />
                                                </div>
                                            </div>

                                            {canEdit && (
                                                <button
                                                    onClick={() => setShowBulkImport(true)}
                                                    disabled={!selectedEndpointTarget}
                                                    className="flex items-center justify-center gap-2.5 px-8 py-2.5 bg-[#1a1f37] hover:bg-[#252c4a] disabled:opacity-30 disabled:grayscale text-white text-sm font-bold rounded-xl transition-all shadow-md shadow-gray-200 whitespace-nowrap shrink-0 w-full md:w-auto active:scale-95"
                                                >
                                                    <FiPlus className="w-4 h-4" /> Add / Import
                                                </button>
                                            )}
                                        </div>
                                    </div>
                                </div>

                                {selectedEndpointTarget ? (
                                    <div className="space-y-4">
                                        {/* Endpoint Search Bar */}
                                        <div className="flex items-center gap-3 bg-white p-3 rounded-xl border border-gray-100 shadow-sm transition-all focus-within:border-blue-300 focus-within:ring-4 focus-within:ring-blue-500/5">
                                            <div className="text-gray-400 pl-1">
                                                <FiSearch className="w-4 h-4" />
                                            </div>
                                            <input
                                                type="text"
                                                placeholder="Search endpoints, methods, types or notes..."
                                                value={endpointSearchQuery}
                                                onChange={(e) => setEndpointSearchQuery(e.target.value)}
                                                className="flex-1 bg-transparent border-none text-sm font-medium text-gray-900 placeholder:text-gray-400 focus:ring-0 outline-none"
                                            />
                                            {endpointSearchQuery && (
                                                <button
                                                    onClick={() => setEndpointSearchQuery("")}
                                                    className="p-1 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-50 transition-colors"
                                                >
                                                    <FiX className="w-3.5 h-3.5" />
                                                </button>
                                            )}
                                        </div>

                                        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
                                            <div className="overflow-x-auto">
                                                <table className="w-full">
                                                    <thead className="bg-gray-50 border-b border-gray-100">
                                                        <tr>
                                                            <th className="px-6 py-4 text-left text-[10px] font-black text-gray-400 uppercase tracking-widest">Resource</th>
                                                            <th className="px-6 py-4 text-left text-[10px] font-black text-gray-400 uppercase tracking-widest">Type</th>
                                                            <th className="px-6 py-4 text-left text-[10px] font-black text-gray-400 uppercase tracking-widest">Method</th>
                                                            <th className="px-6 py-4 text-left text-[10px] font-black text-gray-400 uppercase tracking-widest">Status</th>
                                                            <th className="px-6 py-4 text-left text-[10px] font-black text-gray-400 uppercase tracking-widest">Discovery</th>
                                                            <th className="px-6 py-4 text-right text-[10px] font-black text-gray-400 uppercase tracking-widest">Action</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody className="divide-y divide-gray-100">
                                                        {endpointsLoading ? (
                                                            <tr>
                                                                <td colSpan={6} className="px-6 py-12 text-center">
                                                                    <div className="w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto"></div>
                                                                    <p className="text-sm text-gray-500 mt-4 font-medium tracking-tight">Synchronizing endpoint graph...</p>
                                                                </td>
                                                            </tr>
                                                        ) : filteredEndpoints.length === 0 ? (
                                                            <tr>
                                                                <td colSpan={6} className="px-6 py-16 text-center">
                                                                    <div className="p-4 bg-gray-50 rounded-2xl w-fit mx-auto mb-4">
                                                                        <FiLink className="w-8 h-8 text-gray-300" />
                                                                    </div>
                                                                    <p className="text-gray-900 font-bold">{endpointSearchQuery ? "No endpoints match your search" : "No endpoints mapped"}</p>
                                                                    <p className="text-sm text-gray-400 mt-1 max-w-[240px] mx-auto">{endpointSearchQuery ? "Try a different search term." : "Use the bulk importer to define the testing surface for this target."}</p>
                                                                </td>
                                                            </tr>
                                                        ) : (
                                                            paginatedEndpoints.map((ep) => (
                                                            <tr key={ep.resource_id} className="hover:bg-blue-50/30 transition-colors group">
                                                                <td className="px-6 py-4">
                                                                    <div className="flex items-center gap-3">
                                                                        <div className="w-8 h-8 bg-blue-50 text-blue-600 rounded-lg flex items-center justify-center font-bold text-xs">
                                                                            <FiGlobe className="w-4 h-4" />
                                                                        </div>
                                                                        <div>
                                                                            {editingEndpoint?.resource_id === ep.resource_id ? (
                                                                                <div className="flex flex-col gap-1">
                                                                                    <input
                                                                                        type="text"
                                                                                        value={editingEndpoint.value}
                                                                                        onChange={(e) => setEditingEndpoint({ ...editingEndpoint, value: e.target.value })}
                                                                                        className="text-sm font-mono p-1 border rounded bg-white w-full focus:ring-1 focus:ring-blue-500 outline-none"
                                                                                    />
                                                                                    <input
                                                                                        type="text"
                                                                                        value={editingEndpoint.notes || ""}
                                                                                        placeholder="Notes"
                                                                                        onChange={(e) => setEditingEndpoint({ ...editingEndpoint, notes: e.target.value })}
                                                                                        className="text-[10px] p-1 border rounded bg-white w-full focus:ring-1 focus:ring-blue-500 outline-none"
                                                                                    />
                                                                                </div>
                                                                            ) : (
                                                                                <>
                                                                                    <p className="text-sm font-mono font-medium text-gray-900">{ep.value}</p>
                                                                                    {ep.notes && <p className="text-[10px] text-gray-400 mt-0.5">{ep.notes}</p>}
                                                                                </>
                                                                            )}
                                                                        </div>
                                                                    </div>
                                                                </td>
                                                                <td className="px-6 py-4">
                                                                    <span className="px-2 py-1 bg-gray-100 text-gray-600 text-[10px] font-bold rounded uppercase">
                                                                        {ep.resource_type}
                                                                    </span>
                                                                </td>
                                                                <td className="px-6 py-4">
                                                                    {ep.http_method ? (
                                                                        <span className="px-2 py-1 bg-indigo-50 text-indigo-600 border border-indigo-100 text-[10px] font-black rounded uppercase tracking-tighter">
                                                                            {ep.http_method}
                                                                        </span>
                                                                    ) : (
                                                                        <span className="text-gray-300 font-bold text-[10px] uppercase tracking-widest">—</span>
                                                                    )}
                                                                </td>
                                                                <td className="px-6 py-4">
                                                                    <select
                                                                        value={ep.status}
                                                                        onChange={(e) => handleEndpointStatusChange(ep.resource_id, e.target.value)}
                                                                        disabled={!canEdit}
                                                                        className={`text-[10px] font-black uppercase tracking-widest bg-transparent border-none focus:ring-0 cursor-pointer p-0 h-auto w-auto ${ep.status === 'IN_SCOPE' ? 'text-green-600' : ep.status === 'OUT_OF_SCOPE' ? 'text-red-500' : 'text-gray-400'} disabled:cursor-not-allowed`}
                                                                    >
                                                                        <option value="IN_SCOPE">IN SCOPE</option>
                                                                        <option value="OUT_OF_SCOPE">OUT OF SCOPE</option>
                                                                        <option value="EXCLUDED">EXCLUDED</option>
                                                                        <option value="TESTED">TESTED</option>
                                                                    </select>
                                                                </td>
                                                                <td className="px-6 py-4">
                                                                    <div className="flex items-center gap-2">
                                                                        <div className="flex -space-x-2">
                                                                            {ep.creator?.pfp ? (
                                                                                <img src={ep.creator.pfp} className="w-5 h-5 rounded-full border border-white bg-white" title={ep.creator.display_name} />
                                                                            ) : (
                                                                                <div className="w-5 h-5 rounded-full bg-slate-200 border border-white flex items-center justify-center text-[8px] font-bold" title={ep.creator?.display_name || 'System'}>
                                                                                    {(ep.creator?.display_name || 'S')[0]}
                                                                                </div>
                                                                            )}
                                                                        </div>
                                                                        <span className="text-[10px] font-bold text-gray-400 uppercase tracking-tighter">
                                                                            {ep.discovery_method || 'Manual'}
                                                                        </span>
                                                                    </div>
                                                                </td>
                                                                <td className="px-6 py-4 text-right">
                                                                    {canEdit && (
                                                                        <div className="flex items-center justify-end gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                                                                            {editingEndpoint?.resource_id === ep.resource_id ? (
                                                                                <>
                                                                                    <button
                                                                                        onClick={() => handleUpdateEndpoint(ep.resource_id, { value: editingEndpoint.value, notes: editingEndpoint.notes })}
                                                                                        className="p-1.5 bg-green-50 text-green-600 rounded-lg hover:bg-green-100 transition-colors"
                                                                                        title="Save"
                                                                                    >
                                                                                        <FiCheck className="w-4 h-4" />
                                                                                    </button>
                                                                                    <button
                                                                                        onClick={() => setEditingEndpoint(null)}
                                                                                        className="p-1.5 bg-gray-50 text-gray-400 rounded-lg hover:bg-gray-100 transition-colors"
                                                                                        title="Cancel"
                                                                                    >
                                                                                        <FiX className="w-4 h-4" />
                                                                                    </button>
                                                                                </>
                                                                            ) : (
                                                                                <>
                                                                                    <button
                                                                                        onClick={() => setEditingEndpoint(ep)}
                                                                                        disabled={ep.is_linked}
                                                                                        className={`p-2 rounded-lg transition-colors ${ep.is_linked ? 'text-gray-200 cursor-not-allowed' : 'text-gray-400 hover:text-blue-600 hover:bg-blue-50'}`}
                                                                                        title={ep.is_linked ? "Cannot edit: Endpoint connected to program" : "Edit Endpoint"}
                                                                                    >
                                                                                        <FiEdit2 className="w-4 h-4" />
                                                                                    </button>
                                                                                    <button
                                                                                        onClick={() => handleDeleteEndpoint(ep.resource_id)}
                                                                                        disabled={ep.is_linked}
                                                                                        className={`p-2 rounded-lg transition-colors ${ep.is_linked ? 'text-gray-200 cursor-not-allowed' : 'text-gray-400 hover:text-red-600 hover:bg-red-50'}`}
                                                                                        title={ep.is_linked ? "Cannot delete: Endpoint connected to program" : "Delete Endpoint"}
                                                                                    >
                                                                                        <FiTrash2 className="w-4 h-4" />
                                                                                    </button>
                                                                                </>
                                                                            )}
                                                                        </div>
                                                                    )}
                                                                </td>
                                                            </tr>
                                                        ))
                                                    )}
                                                </tbody>
                                            </table>
                                        </div>
                                        {/* Endpoint Pagination Footer */}
                                        {totalEndpointPages > 1 && (
                                            <div className="px-6 py-4 bg-gray-50 border-t border-gray-100 flex items-center justify-between">
                                                <p className="text-xs text-gray-500 font-medium font-bold uppercase tracking-wider">
                                                    Showing <span className="text-gray-900 font-bold">{(endpointPage - 1) * PAGE_SIZE + 1}</span> to <span className="text-gray-900 font-bold">{Math.min(endpointPage * PAGE_SIZE, filteredEndpoints.length)}</span> of <span className="text-gray-900 font-bold">{filteredEndpoints.length}</span> endpoints
                                                </p>
                                                <div className="flex items-center gap-2">
                                                    <button
                                                        onClick={() => setEndpointPage(p => Math.max(1, p - 1))}
                                                        disabled={endpointPage === 1}
                                                        className="p-1.5 rounded-lg border border-gray-200 bg-white text-gray-400 hover:text-blue-600 hover:border-blue-100 disabled:opacity-30 transition-all font-bold"
                                                    >
                                                        <FiChevronLeft className="w-4 h-4" />
                                                    </button>
                                                    <div className="flex items-center gap-1">
                                                        {getPaginationRange(endpointPage, totalEndpointPages).map((item, i) => {
                                                            if (item === "...") {
                                                                return <span key={`ellipsis-${i}`} className="text-slate-300 px-1 font-bold">...</span>;
                                                            }
                                                            const pageNum = item as number;
                                                            return (
                                                                <button
                                                                    key={pageNum}
                                                                    onClick={() => setEndpointPage(pageNum)}
                                                                    className={`w-8 h-8 rounded-lg text-xs font-bold transition-all ${endpointPage === pageNum ? 'bg-blue-600 text-white shadow-md' : 'bg-white text-gray-500 border border-gray-100 hover:border-blue-100 hover:text-blue-600'}`}
                                                                >
                                                                    {pageNum}
                                                                </button>
                                                            );
                                                        })}
                                                    </div>
                                                    <button
                                                        onClick={() => setEndpointPage(p => Math.min(totalEndpointPages, p + 1))}
                                                        disabled={endpointPage === totalEndpointPages}
                                                        className="p-1.5 rounded-lg border border-gray-200 bg-white text-gray-400 hover:text-blue-600 hover:border-blue-100 disabled:opacity-30 transition-all font-bold"
                                                    >
                                                        <FiChevronRight className="w-4 h-4" />
                                                    </button>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                </div>
                                ) : (
                                    <div className="bg-white rounded-3xl border border-dashed border-gray-200 p-24 text-center">
                                        <div className="w-20 h-20 bg-blue-50 rounded-full flex items-center justify-center mx-auto mb-6">
                                            <FiSearch className="w-8 h-8 text-blue-300" />
                                        </div>
                                        <h3 className="text-2xl font-black text-gray-900 tracking-tighter">Selection Required</h3>
                                        <p className="text-gray-500 mt-3 max-w-sm mx-auto text-sm font-medium leading-relaxed">
                                            To manage endpoints and define the attack surface, please first select a target from the identity registry above.
                                        </p>
                                    </div>
                                )}
                            </div>
                        )}

                        {activeTab === "documentation" && (
                            <div className="space-y-8 animate-in fade-in slide-in-from-bottom-4 duration-500 pb-20">
                                {/* Auto-save status indicator */}
                                <div className="flex items-center justify-between mb-4">
                                    <div className="flex items-center gap-2">
                                        <div className="p-2 bg-blue-50 text-blue-600 rounded-lg">
                                            <FiBook className="w-5 h-5" />
                                        </div>
                                        <div>
                                            <h3 className="text-xl font-bold text-[#1a1f37]">Asset Documentation</h3>
                                            <p className="text-sm text-gray-400 font-medium">Categorized technical documentation and links</p>
                                        </div>
                                    </div>
                                    {isSaving ? (
                                        <div className="flex items-center gap-2 text-blue-600 bg-blue-50 px-4 py-2 rounded-full text-sm font-bold animate-pulse">
                                            <FiRefreshCw className="animate-spin w-4 h-4" />
                                            Saving changes...
                                        </div>
                                    ) : (
                                        <div className="flex items-center gap-2 text-green-600 bg-green-50 px-4 py-2 rounded-full text-sm font-bold">
                                            <FiCheck className="w-4 h-4" />
                                            All changes saved
                                        </div>
                                    )}
                                </div>
                                <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                                    {[
                                                { id: 'architecture', title: 'Architecture Diagram', urlKey: 'architecture_diagram_url', icon: <FiCpu className="w-4 h-4" /> },
                                                { id: 'product_doc', title: 'Product Documentation', urlKey: 'product_documentation_url', icon: <FiFileText className="w-4 h-4" /> },
                                                { id: 'runbook', title: 'Runbook / Deployment Guide', urlKey: 'runbook_url', icon: <FiBook className="w-4 h-4" /> },
                                                { id: 'threat_model', title: 'Threat Model / Risk Assessment', urlKey: 'threat_model_url', icon: <FiShield className="w-4 h-4" /> }
                                            ].map((section) => (
                                                <div key={section.id} className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden flex flex-col p-6 transition-all hover:shadow-md">
                                                    <div className="flex items-center justify-between mb-6">
                                                        <div className="flex items-center gap-3">
                                                            <div className="w-9 h-9 bg-slate-50 text-slate-400 rounded-xl flex items-center justify-center shadow-sm border border-slate-100">
                                                                {section.icon}
                                                            </div>
                                                            <div>
                                                                <h4 className="text-sm font-bold text-slate-800">{section.title}</h4>
                                                                <p className="text-[10px] text-slate-400 font-bold uppercase tracking-wider">Resource Asset</p>
                                                            </div>
                                                        </div>
                                                    </div>

                                            {/* Link Tag UI */}
                                                    <div className="mb-6">
                                                        <label className="text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-2 flex items-center gap-2">
                                                            <FiLink className="w-3 h-3" />
                                                            Links
                                                        </label>
                                                        
                                                        <div className="flex flex-wrap gap-1.5 mb-3">
                                                            {(docData[section.urlKey as keyof typeof docData] as string[] || []).map((url, i) => (
                                                                <div key={i} className="group/link flex items-center gap-2 px-2.5 py-1.5 bg-blue-50/50 border border-blue-100/50 rounded-lg text-xs font-semibold text-blue-600 transition-all hover:bg-blue-50">
                                                                    <FiExternalLink className="w-3 h-3 opacity-50" />
                                                                    <a href={url} target="_blank" rel="noopener noreferrer" className="hover:underline truncate max-w-[150px]">
                                                                        {url.replace(/^https?:\/\//, '').split('/')[0]}
                                                                    </a>
                                                                    {canEdit && (
                                                                        <button 
                                                                            onClick={() => handleRemoveLink(section.urlKey as any, url)}
                                                                            className="ml-1 p-0.5 hover:bg-blue-100 rounded-md text-blue-300 hover:text-blue-500 transition-all"
                                                                        >
                                                                            <FiX className="w-3 h-3" />
                                                                        </button>
                                                                    )}
                                                                </div>
                                                            ))}
                                                            {(docData[section.urlKey as keyof typeof docData] as string[] || []).length === 0 && (
                                                                <p className="text-[11px] text-slate-400 font-medium italic">No links added</p>
                                                            )}
                                                        </div>

                                                        {canEdit && (
                                                            <div className="relative group">
                                                                <input
                                                                    type="url"
                                                                    placeholder="Quick add URL..."
                                                                    onKeyDown={(e) => {
                                                                        if (e.key === 'Enter') {
                                                                            const val = e.currentTarget.value.trim();
                                                                            if (val) {
                                                                                handleAddLink(section.urlKey as any, val);
                                                                                e.currentTarget.value = '';
                                                                            }
                                                                        }
                                                                    }}
                                                                    className="w-full pl-4 pr-10 py-2 bg-slate-50 border border-slate-100 rounded-xl text-xs font-medium transition-all focus:bg-white focus:border-blue-400 outline-none placeholder:text-slate-300 shadow-inner"
                                                                />
                                                                <div className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-300">
                                                                    <FiCornerDownLeft className="w-3.5 h-3.5" />
                                                                </div>
                                                            </div>
                                                        )}
                                                    </div>

                                            {/* File Tags UI */}
                                                    <div className="flex-1 flex flex-col min-h-0">
                                                        <label className="text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-2 flex items-center gap-2">
                                                            <FiPaperclip className="w-3 h-3" />
                                                            Files
                                                        </label>
                                                        
                                                        <div className="space-y-2 mb-3">
                                                            {docData.documentation_files.filter(f => f.category === section.id).map((file, idx) => {
                                                                const globalIdx = docData.documentation_files.indexOf(file);
                                                                return (
                                                                    <div key={idx} className="flex items-center justify-between p-2.5 bg-slate-50/50 rounded-xl group/file hover:bg-white border border-slate-100 hover:border-blue-100 hover:shadow-sm transition-all">
                                                                        <div className="flex items-center gap-2.5 overflow-hidden">
                                                                            <div className="shrink-0 w-8 h-8 bg-white rounded-lg shadow-sm flex items-center justify-center text-blue-500 border border-slate-100 group-hover/file:bg-blue-50">
                                                                                <FiFileText className="w-3.5 h-3.5" />
                                                                            </div>
                                                                            <div className="overflow-hidden">
                                                                                <a href={file.url} target="_blank" rel="noopener noreferrer" className="text-xs font-bold text-slate-700 truncate block hover:text-blue-600 transition-colors tracking-tight">
                                                                                    {file.name}
                                                                                </a>
                                                                                <p className="text-[9px] text-slate-400 font-bold uppercase tracking-wider">{file.type?.split('/')[1] || 'FILE'}</p>
                                                                            </div>
                                                                        </div>
                                                                        {canEdit && (
                                                                            <button 
                                                                                onClick={() => removeFile(globalIdx)}
                                                                                className="p-1.5 text-slate-300 hover:text-red-500 hover:bg-red-50 rounded-lg transition-all opacity-0 group-hover/file:opacity-100"
                                                                            >
                                                                                <FiTrash2 className="w-3.5 h-3.5" />
                                                                            </button>
                                                                        )}
                                                                    </div>
                                                                );
                                                            })}
                                                            {docData.documentation_files.filter(f => f.category === section.id).length === 0 && (
                                                                <div className="py-4 text-center border border-dashed border-slate-100 rounded-xl bg-slate-50/20">
                                                                    <p className="text-[11px] text-slate-300 font-medium italic">No files attached</p>
                                                                </div>
                                                            )}
                                                        </div>

                                                        {canEdit && (
                                                            <div className="relative group/up mt-auto">
                                                                <input
                                                                    type="file"
                                                                    onChange={(e) => handleFileUpload(e, section.id)}
                                                                    disabled={uploading}
                                                                    className="absolute inset-0 w-full h-full opacity-0 cursor-pointer z-10"
                                                                    accept=".pdf,.png,.jpg,.jpeg,.doc,.docx"
                                                                />
                                                                <div className={`py-4 border shadow-inner rounded-xl flex items-center justify-center gap-2 transition-all duration-300 ${uploading && activeCategory === section.id ? 'bg-blue-50 border-blue-200' : 'bg-white border-slate-100 group-hover/up:border-blue-200 group-hover/up:bg-blue-50/30'}`}>
                                                                    {uploading && activeCategory === section.id ? (
                                                                        <FiRefreshCw className="animate-spin w-4 h-4 text-blue-500" />
                                                                    ) : (
                                                                        <>
                                                                            <FiUploadCloud className="w-4 h-4 text-slate-300 group-hover/up:text-blue-400 transition-colors" />
                                                                            <span className="text-[11px] font-bold text-slate-400 group-hover/up:text-blue-500 transition-colors">Attach File</span>
                                                                        </>
                                                                    )}
                                                                </div>
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>
                                            ))}
                                        </div>

                                {/* Other Attachments (uncategorized or general) */}
                                <div className="bg-white rounded-2xl p-8 border border-slate-100 shadow-sm transition-all hover:shadow-md">
                                    <div className="flex items-center justify-between mb-6">
                                        <div className="flex items-center gap-3">
                                            <div className="w-9 h-9 bg-slate-50 text-slate-400 rounded-xl flex items-center justify-center shadow-sm border border-slate-100">
                                                <FiPaperclip className="w-4 h-4" />
                                            </div>
                                            <div>
                                                <h4 className="text-sm font-bold text-slate-800">Other Attachments</h4>
                                                <p className="text-[10px] text-slate-400 font-bold uppercase tracking-wider">General Files</p>
                                            </div>
                                        </div>
                                    </div>

                                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3 mb-6">
                                        {docData.documentation_files.filter(f => !f.category).map((file, idx) => {
                                            const globalIdx = docData.documentation_files.indexOf(file);
                                            return (
                                                <div key={idx} className="bg-slate-50/50 border border-slate-100 rounded-xl p-3 flex items-center justify-between group hover:bg-white hover:border-blue-100 hover:shadow-sm transition-all duration-300">
                                                    <div className="flex items-center gap-3 overflow-hidden">
                                                        <div className="shrink-0 w-8 h-8 bg-white rounded-lg shadow-sm flex items-center justify-center text-blue-500 border border-slate-100 group-hover:bg-blue-50">
                                                            <FiFileText className="w-3.5 h-3.5" />
                                                        </div>
                                                        <div className="overflow-hidden">
                                                            <a href={file.url} target="_blank" rel="noopener noreferrer" className="text-xs font-bold text-slate-700 truncate block hover:text-blue-600 transition-colors tracking-tight">
                                                                {file.name}
                                                            </a>
                                                            <p className="text-[9px] text-slate-400 font-bold uppercase tracking-wider">{file.type?.split('/')[1] || 'FILE'}</p>
                                                        </div>
                                                    </div>
                                                    {canEdit && (
                                                        <button 
                                                            onClick={() => removeFile(globalIdx)}
                                                            className="p-1.5 text-slate-200 hover:text-red-500 hover:bg-red-50 rounded-lg transition-all opacity-0 group-hover:opacity-100"
                                                        >
                                                            <FiTrash2 className="w-3.5 h-3.5" />
                                                        </button>
                                                    )}
                                                </div>
                                            );
                                        })}
                                        {docData.documentation_files.filter(f => !f.category).length === 0 && (
                                            <div className="col-span-full py-4 text-center border border-dashed border-slate-100 rounded-xl bg-slate-50/20">
                                                <p className="text-[11px] text-slate-300 font-medium italic">No general attachments found</p>
                                            </div>
                                        )}
                                    </div>

                                    {canEdit && (
                                        <div className="relative group/up max-w-[200px]">
                                            <input
                                                type="file"
                                                onChange={(e) => handleFileUpload(e)}
                                                disabled={uploading}
                                                className="absolute inset-0 w-full h-full opacity-0 cursor-pointer z-10"
                                                accept=".pdf,.png,.jpg,.jpeg,.doc,.docx"
                                            />
                                            <div className={`py-2.5 px-4 border border-slate-200 shadow-sm rounded-xl flex items-center justify-center gap-2 transition-all duration-300 ${uploading && !activeCategory ? 'bg-blue-50 border-blue-200' : 'bg-white border-slate-200 group-hover/up:border-blue-400 group-hover/up:bg-blue-50/10'}`}>
                                                {uploading && !activeCategory ? (
                                                    <FiRefreshCw className="animate-spin w-3.5 h-3.5 text-blue-500" />
                                                ) : (
                                                    <>
                                                        <FiUploadCloud className="w-3.5 h-3.5 text-slate-400 group-hover/up:text-blue-500" />
                                                        <span className="text-xs font-bold text-slate-500 group-hover/up:text-blue-600">Quick Upload</span>
                                                    </>
                                                )}
                                            </div>
                                        </div>
                                    )}
                                </div>

                                {/* Additional Notes Section */}
                                <div className="bg-white rounded-2xl overflow-hidden flex flex-col shadow-sm border border-slate-100 transition-all hover:shadow-md">
                                    <div className="p-8 flex-1">
                                        <div className="flex items-center justify-between mb-6">
                                            <h3 className="text-sm font-bold text-slate-800 flex items-center gap-3">
                                                <div className="p-2 bg-slate-50 rounded-lg text-blue-500 border border-slate-100">
                                                    <FiEdit3 className="w-4 h-4" />
                                                </div>
                                                Additional Technical Notes
                                            </h3>
                                            <div className="px-3 py-1 bg-slate-50 rounded-full border border-slate-100 text-[9px] font-bold text-slate-400 uppercase tracking-widest">
                                                Markdown Supported
                                            </div>
                                        </div>

                                        <div className="text-[12px] text-slate-500 mb-6 font-medium leading-relaxed bg-slate-50/50 p-4 rounded-xl border border-slate-100/50">
                                            <p className="mb-2 text-slate-400 font-bold uppercase tracking-[0.1em] text-[9px] flex items-center gap-2">
                                                <span className="w-1.5 h-1.5 rounded-full bg-blue-500"></span>
                                                Documentation Tips:
                                            </p>
                                            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-1 opacity-80">
                                                <div className="flex items-center gap-2">
                                                    <span className="w-1 h-1 rounded-full bg-blue-400/40"></span>
                                                    Describe architecture basics
                                                </div>
                                                <div className="flex items-center gap-2">
                                                    <span className="w-1 h-1 rounded-full bg-blue-400/40"></span>
                                                    List shared dependencies
                                                </div>
                                                <div className="flex items-center gap-2">
                                                    <span className="w-1 h-1 rounded-full bg-blue-400/40"></span>
                                                    Note legacy components
                                                </div>
                                                <div className="flex items-center gap-2">
                                                    <span className="w-1 h-1 rounded-full bg-blue-400/40"></span>
                                                    Detail infra nuances
                                                </div>
                                            </div>
                                        </div>

                                        <div className="relative">
                                            {!canEdit ? (
                                                <div className="w-full text-slate-700 text-sm leading-relaxed font-normal">
                                                    <ReadMoreText
                                                        text={docData.additional_notes || "No additional notes found."}
                                                        maxLines={8}
                                                        className="text-slate-700 text-sm font-normal leading-relaxed"
                                                    />
                                                </div>
                                            ) : (
                                                <textarea
                                                    name="additional_notes"
                                                    value={docData.additional_notes}
                                                    onChange={handleDocChange}
                                                    rows={8}
                                                    className="w-full bg-transparent border-none text-slate-700 text-sm leading-relaxed placeholder-slate-300 focus:ring-0 outline-none resize-none font-normal p-0 custom-scrollbar"
                                                    placeholder="Document technical nuances, shared dependencies, or infrastructure context..."
                                                />
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}

                        {activeTab === "history" && canEdit && (
                            <div className="space-y-6 animate-in fade-in slide-in-from-bottom-4 duration-500">
                                <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
                                    <div className="flex items-center justify-between mb-6">
                                        <div className="flex items-center gap-3">
                                            <div className="p-2 bg-blue-50 text-blue-600 rounded-xl">
                                                <FiActivity className="w-4 h-4" />
                                            </div>
                                            <div>
                                                <h3 className="text-sm font-bold text-[#1a1f37]">Activity Timeline</h3>
                                                <p className="text-[11px] text-gray-400 font-medium">Tracking modifications and transitions</p>
                                            </div>
                                        </div>
                                        <button 
                                            onClick={fetchActivities}
                                            disabled={activitiesLoading}
                                            className="p-1.5 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-all"
                                            title="Refresh Timeline"
                                        >
                                            <FiRefreshCw className={`w-4 h-4 ${activitiesLoading ? 'animate-spin' : ''}`} />
                                        </button>
                                    </div>

                                    {activitiesLoading && activities.length === 0 ? (
                                        <div className="py-12 text-center">
                                            <div className="w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
                                            <p className="text-xs text-gray-500 font-medium">Retrieving logs...</p>
                                        </div>
                                    ) : activities.length === 0 ? (
                                        <div className="py-12 text-center bg-gray-50/50 rounded-xl border border-dashed border-gray-200">
                                            <FiActivity className="w-8 h-8 text-gray-200 mx-auto mb-3" />
                                            <p className="text-sm text-gray-500 font-bold">No activity logs</p>
                                            <p className="text-[11px] text-gray-400 mt-1">Actions performed on this asset will appear here.</p>
                                        </div>
                                    ) : (
                                        <div className="relative">
                                            {/* Vertical Line */}
                                            <div className="absolute left-[17px] top-2 bottom-2 w-0.5 bg-slate-50"></div>

                                            <div className="space-y-4">
                                                {(() => {
                                                    const pageSize = 15;
                                                    const startIndex = (historyPage - 1) * pageSize;
                                                    const paginatedActivities = activities.slice(startIndex, startIndex + pageSize);
                                                    const totalPages = Math.ceil(activities.length / pageSize);

                                                    return (
                                                        <>
                                                            {paginatedActivities.map((log, idx) => (
                                                                <div key={log.id} className="relative pl-10 group">
                                                                    {/* Dot */}
                                                                    <div className="absolute left-0 top-1 w-9 h-9 flex items-center justify-center z-10">
                                                                        <div className="w-2.5 h-2.5 rounded-full border-[3px] border-white bg-blue-500 shadow-sm ring-4 ring-blue-50/50"></div>
                                                                    </div>

                                                                    <div className="bg-white group-hover:bg-slate-50/30 p-3.5 rounded-xl border border-gray-100 transition-all duration-300">
                                                                        <div className="flex flex-col md:flex-row md:items-center justify-between gap-3">
                                                                            <div className="flex items-center gap-3">
                                                                                <div className="w-8 h-8 rounded-full bg-slate-100 border border-white shadow-sm overflow-hidden shrink-0">
                                                                                    {log.user?.pfp ? (
                                                                                        <img src={log.user.pfp} alt={log.user.display_name} className="w-full h-full object-cover" />
                                                                                    ) : (
                                                                                        <div className="w-full h-full flex items-center justify-center text-slate-400 bg-slate-50">
                                                                                            <FiUsers className="w-4 h-4" />
                                                                                        </div>
                                                                                    )}
                                                                                </div>
                                                                                <div>
                                                                                    <p className="text-sm font-bold text-slate-800 leading-tight mb-1">
                                                                                        {log.action}
                                                                                    </p>
                                                                                    <div className="flex items-center gap-2">
                                                                                        <span className="text-[9px] font-bold text-blue-600 bg-blue-50 px-1.5 py-0.5 rounded uppercase tracking-wider">
                                                                                            {log.module}
                                                                                        </span>
                                                                                        <span className="text-gray-300 text-[10px]">•</span>
                                                                                        <span className="text-[11px] font-semibold text-gray-400">
                                                                                            by <span className="text-gray-600">{log.user?.display_name || log.user?.username || 'Unknown User'}</span>
                                                                                        </span>
                                                                                    </div>
                                                                                </div>
                                                                            </div>
                                                                            <div className="flex items-center gap-2 text-gray-400 shrink-0">
                                                                                <FiClock className="w-3 h-3" />
                                                                                <span className="text-xs font-bold text-slate-400">
                                                                                    {format(new Date(log.created_at), "MMM dd, yyyy • HH:mm")}
                                                                                </span>
                                                                            </div>
                                                                        </div>
                                                                    </div>
                                                                </div>
                                                            ))}

                                                            {/* Pagination Controls */}
                                                            {totalPages > 1 && (
                                                                <div className="mt-8 flex items-center justify-between bg-slate-50/50 p-3 rounded-xl border border-slate-100">
                                                                    <div className="text-[11px] font-bold text-slate-400 uppercase tracking-wider">
                                                                        Showing {startIndex + 1}-{Math.min(startIndex + pageSize, activities.length)} of {activities.length} entries
                                                                    </div>
                                                                    <div className="flex items-center gap-1.5">
                                                                        <button
                                                                            onClick={() => setHistoryPage(p => Math.max(1, p - 1))}
                                                                            disabled={historyPage === 1}
                                                                            className="p-1.5 rounded-lg border border-slate-100 bg-white text-slate-400 hover:text-blue-600 hover:border-blue-100 disabled:opacity-30 disabled:grayscale transition-all"
                                                                        >
                                                                            <FiChevronLeft className="w-4 h-4" />
                                                                        </button>
                                                                        
                                                                        <div className="flex items-center gap-1">
                                                                            {getPaginationRange(historyPage, totalPages).map((item, i) => {
                                                                                if (item === "...") {
                                                                                    return <span key={`ellipsis-${i}`} className="text-slate-300 px-1 font-bold">...</span>;
                                                                                }
                                                                                const p = item as number;
                                                                                return (
                                                                                    <button
                                                                                        key={p}
                                                                                        onClick={() => setHistoryPage(p)}
                                                                                        className={`min-w-[32px] h-8 rounded-lg text-xs font-bold transition-all ${historyPage === p ? 'bg-blue-600 text-white shadow-md shadow-blue-100' : 'bg-white text-slate-500 border border-slate-100 hover:border-blue-100 hover:text-blue-600'}`}
                                                                                    >
                                                                                        {p}
                                                                                    </button>
                                                                                );
                                                                            })}
                                                                        </div>

                                                                        <button
                                                                            onClick={() => setHistoryPage(p => Math.min(totalPages, p + 1))}
                                                                            disabled={historyPage === totalPages}
                                                                            className="p-1.5 rounded-lg border border-slate-100 bg-white text-slate-400 hover:text-blue-600 hover:border-blue-100 disabled:opacity-30 disabled:grayscale transition-all"
                                                                        >
                                                                            <FiChevronRight className="w-4 h-4" />
                                                                        </button>
                                                                    </div>
                                                                </div>
                                                            )}
                                                        </>
                                                    );
                                                })()}
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>




            {/* Modals */}
            <AnimatePresence>
                {
                    showWizard && asset && (
                        <CreateTargetWizard
                            assetId={asset.id}
                            assetName={asset.name}
                            assetCategory={asset.category === "Other" ? asset.category_other || "Other" : asset.category}
                            assetType={asset.primary_type}
                            mobilePlatforms={asset.mobile_platforms}
                            onClose={() => setShowWizard(false)}
                            onSuccess={handleTargetAdditionSuccess}
                        />
                    )
                }
            </AnimatePresence>

            <AnimatePresence>
                {showResourceManager && selectedTargetId && asset && (
                    <ResourceManager
                        assetId={asset.id}
                        targetId={selectedTargetId}
                        onClose={() => {
                            setShowResourceManager(false);
                            setSelectedTargetId(null);
                        }}
                        readOnly={!canEdit}
                    />
                )}
            </AnimatePresence>

            <AnimatePresence>
                {showEditModal && editingTarget && (
                    <EditTargetModal
                        target={editingTarget}
                        assetId={id || ""}
                        onClose={() => setShowEditModal(false)}
                        onSuccess={handleTargetRefresh}
                    />
                )}
            </AnimatePresence>

            <AnimatePresence>
                {showBulkImport && id && selectedEndpointTarget && asset && (
                    <BulkImportModal
                        assetId={id}
                        assetName={asset.name}
                        targetId={selectedEndpointTarget}
                        targetName={targets.find(t => t.target_id === selectedEndpointTarget)?.target_name || "Unknown Target"}
                        assetCategory={asset.category}
                        onClose={() => setShowBulkImport(false)}
                        onSuccess={() => {
                            setShowBulkImport(false);
                            fetchEndpoints(selectedEndpointTarget);
                            handleTargetRefresh();
                        }}
                    />
                )}
            </AnimatePresence>
        </motion.div >
    );
};

export default AssetDetailPage;
